import { useEffect, useRef, useState } from 'react'
import { setUrlParams } from '../../utils/urlState.js'
import {
  ArrowLeft, BookmarkPlus, CalendarClock, Check, CheckCheck, Copy, Download, ExternalLink, Eye, EyeOff, FileArchive,
  FileClock, FileText, FolderArchive, FolderUp, Hourglass, Inbox, Link2, Loader2, Lock,
  Pencil, PenLine, Plus, RotateCcw, Send, Share2, ShieldCheck, Trash2, Upload, X,
} from 'lucide-react'
import api from '../../services/api'
import { formatDate, formatDateTime } from '../../utils/helpers'
import { useAuth } from '../../hooks/useAuth'
import { saveFileDraft, loadFileDraft, deleteFileDraft } from '../../services/draftStore.js'

/**
 * Dossiers — "one manages dossiers, not documents".
 *
 * A submission to a donor, a ministry or a regulator is never one file. It is a pack that must be
 * complete, every part approved, and sent as it stood on the day. So the pack is the unit here:
 *
 *   drop in the files (or start from a ready-made pack)  →  check the names it proposes  →  verify,
 *   review, approve (signed with your name; one at a time or all together)  →  submit  →  download
 *
 * Every document gets an ID naming the project and the type of document (3P-PRJ-00039-PRT-001),
 * and every step is stamped with who and when, on the dossier's own audit trail.
 */

const STEPS = ['Draft', 'Verified', 'Reviewed', 'Approved']

const STATUS_BADGE = {
  'In preparation': 'bg-amber-50 text-amber-800 border-amber-200',
  Ready: 'bg-blue-50 text-blue-700 border-blue-200',
  Submitted: 'bg-green-50 text-green-700 border-green-200',
  Accepted: 'bg-green-50 text-green-700 border-green-200',
  Destroyed: 'bg-red-50 text-red-700 border-red-200',
  Archived: 'bg-slate-100 text-slate-700 border-slate-300',
  Closed: 'bg-gray-50 text-gray-600 border-gray-200',
}

const shownStatus = (d) => (d.destroyed ? 'Destroyed'
  : d.archived ? 'Archived' : d.status === 'Closed' ? 'Closed'
  : d.status === 'Submitted' ? (d.direction === 'incoming' ? 'Accepted' : 'Submitted')
    : d.ready ? 'Ready' : 'In preparation')

// The badge's words. One place for the list and the dossier alike: written twice, the dossier's own page
// told a pack that came in that it was "Ready to submit" while its card said "Ready to accept".
const statusLabel = (d) => {
  const shown = shownStatus(d)
  return shown === 'Ready' ? (d.direction === 'incoming' ? 'Ready to accept' : 'Ready to submit') : shown
}

const ACTION_LABEL = {
  dossier_created: 'Dossier created',
  slot_added: 'Document slot added',
  document_added: 'File added',
  document_linked: 'Existing document used',
  document_verified: 'Verified',
  document_reviewed: 'Reviewed',
  document_approved: 'Approved & signed',
  document_revised: 'Revised for cause',
  document_sent_back: 'Sent back',
  moved_to_submission: 'Moved to the Submission folder',
  move_proposed: 'Proposed for the Submission folder',
  move_agreed: 'Agreed to the move',
  rules_changed: 'Rules changed',
  rules_from_type: 'Rules taken from the dossier type',
  submission_opened: 'Opened by the recipient',
  receipt_confirmed: 'Receipt confirmed by the recipient',
  receipt_recorded: 'Delivery receipt recorded',
  dossier_reopened: 'Reopened',
  dossier_closed: 'Closed unsent',
  dossier_archived: 'Archived and sealed',
  seal_checked: 'Seal checked',
  legal_hold_placed: 'Legal hold placed',
  legal_hold_lifted: 'Legal hold lifted',
  taken_back_to_working: 'Taken back to the Working folder',
  dossier_submitted: 'Dossier submitted',
  dossier_accepted: 'Dossier accepted',
  pack_downloaded: 'Pack downloaded',
  slot_edited: 'Document details corrected',
  recipient_corrected: 'Recipient corrected',
  document_removed: 'File removed',
  slot_removed: 'Document removed',
  references_set: 'Citations updated',
  pack_shared: 'Shared with an outside organisation',
  share_revoked: 'Share link withdrawn',
  share_page_opened: 'Share page opened',
  shared_file_opened: 'File opened through a share link',
  shared_file_downloaded: 'File downloaded through a share link',
  shared_pack_downloaded: 'Whole pack downloaded through a share link',
  dossier_destroyed: 'Destroyed (end of life)',
  dossier_hidden: 'Dossier hidden from the team',
  dossier_shown: 'Dossier shown to the team',
  document_hidden: 'Document hidden from the team',
  document_shown: 'Document shown to the team',
  review_schedule_set: 'Periodic review set',
  periodic_review: 'Periodic review recorded',
  retention_extended: 'Kept longer',
  saved_as_type: 'Saved as a ready-made pack',
  archive_imported: 'Archive imported',
}

// A dossier's rules, in words.
const RULE_LABEL = { approver_id: 'Approver', backup_id: 'Backup', steps: 'Approvals per document',
  move: 'Move to the Submission folder', send_ways: 'How a submission may travel', archivers: 'Records keepers' }
const WAY_LABEL = { link: 'Secure link by email', attachment: 'Files attached to the email', hand: 'Delivered by us' }
const WAY_HINT = {
  link: 'They get an email with a button to a page listing every document, and Download all. Size does not matter.',
  attachment: 'The files ride on the email itself — for recipients whose systems block links. Up to 20 MB.',
  hand: 'Their own portal, a courier or paper. Nothing is emailed; record the reference and the receipt afterwards.',
}
const SUB_BADGE = {
  Submitted: 'bg-blue-50 text-blue-700 border-blue-200', Opened: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Receipt confirmed': 'bg-green-50 text-green-700 border-green-200',
  'Delivered — receipt recorded': 'bg-green-50 text-green-700 border-green-200',
  'Withdrawn — reopened': 'bg-gray-50 text-gray-600 border-gray-200',
}
const ruleText = (k, v) => {
  if (v === null || v === undefined || (Array.isArray(v) && !v.length)) {
    return { approver_id: 'whoever manages the team', backup_id: 'none', archivers: 'the administrator only' }[k] || '—'
  }
  if (k === 'steps') return v === 'three' ? 'Verify → review → approve' : 'One approval'
  if (k === 'move') return v === 'approver' ? 'The approver alone' : 'Every team member agrees'
  if (k === 'send_ways') return v.map(w => WAY_LABEL[w] || w).join(', ')
  if (Array.isArray(v)) return v.join(', ')
  return String(v)
}

// How an approval was signed: typed here, or on paper and recorded here.
const signedText = (it) => (it.paper
  ? `Signed on paper by ${it.paper.signed_by}${it.paper.signed_on ? ` on ${formatDate(it.paper.signed_on)}` : ''}`
    + `${it.paper.original_at ? ` · original kept at ${it.paper.original_at}` : ''} — recorded by ${it.signature}`
  : `Signed “${it.signature}”`)

// A folder brings everything in it, including what the operating system leaves lying about.
const isJunkFile = (f) => /^(\.|~\$)/.test(f.name) || ['thumbs.db', 'desktop.ini'].includes(f.name.toLowerCase())

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail
  if (typeof d === 'string') return d
  if (Array.isArray(d)) return d.map(x => x.msg).join('; ')
  return fallback
}

const saveBlob = (data, filename) => {
  const url = URL.createObjectURL(data)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

// The public link an outside organisation opens. Built from wherever this app reaches its API,
// so it is right on staging, production and a laptop alike.
const shareUrl = (token) =>
  new URL(`${(api.defaults.baseURL || '').replace(/\/$/, '')}/dossier-share/${token}`, window.location.origin).href

function Modal({ title, onClose, children, wide = false, error = null }) {
  // A click on the dark area beside the box does NOTHING. It used to close it, and anything typed or
  // dropped in a half-finished form went with it -- one stray click, or a text selection dragged out
  // past the edge, and a dossier of five files was gone. It closes with its X or its own buttons.
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className={`bg-white rounded-2xl shadow-xl w-full ${wide ? 'max-w-4xl' : 'max-w-md'} max-h-[90vh] overflow-y-auto`}
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          {onClose && <button onClick={onClose} title="Close" className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>}
        </div>
        <div className="p-5">
          {/* A refusal is shown HERE. On the page behind, the overlay hid it -- a wrong signature
              looked like nothing happening. */}
          {error && <p className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
          {children}
        </div>
      </div>
    </div>
  )
}

// ── names from files ─────────────────────────────────────────────────────────
// A title and a type are PROPOSED from each file's name, here in the browser. Nothing is read from
// inside the file and nothing is sent anywhere to do it -- no client document goes near an AI
// model -- and every proposal can be changed before anything is saved.

const MAX_BYTES = 50 * 1024 * 1024

const titleFromFile = (name) => {
  let t = String(name || '').replace(/\.[a-z0-9]{1,5}$/i, '')
  t = t.replace(/[-_.]+/g, ' ').replace(/\(\d+\)/g, ' ')
    .replace(/\b(v|ver|rev|version)\s*\d+\b/gi, ' ').replace(/\bcopy\b/gi, ' ')
    .replace(/^\s*\d{1,3}\s+/, '').replace(/\s+/g, ' ').trim()
  if (!t) t = String(name || 'Document')
  if (t === t.toUpperCase()) t = t.toLowerCase()
  return t.charAt(0).toUpperCase() + t.slice(1)
}

// The type whose longest keyword appears as whole words in the text. "General document" otherwise.
const suggestType = (text, types) => {
  const hay = ` ${String(text || '').toLowerCase().replace(/[^a-z0-9']+/g, ' ')} `
  let best = 'GEN'
  let score = 0
  for (const t of types) {
    for (const k of t.keywords || []) {
      if (k.length > score && hay.includes(` ${k} `)) { best = t.code; score = k.length }
    }
  }
  return best
}

// "Other": a code made from the title's initials, so nobody has to invent one.
const codeFromTitle = (title) => {
  const words = String(title || '').toUpperCase().replace(/[^A-Z0-9 ]+/g, ' ').split(/\s+/).filter(Boolean)
  let code = words.length > 1 ? words.slice(0, 3).map(w => w[0]).join('') : (words[0] || 'DOC').slice(0, 3)
  while (code.length < 3) code += 'X'
  return code
}

const effectiveCode = (row) => (row.type === '__title' ? codeFromTitle(row.title) : row.type)

// Unique across visits: a draft brought back from the browser carries the keys it had, and a counter
// that starts from 0 on every page load would hand out the same ones again.
let rowSeq = 0
const newRow = (patch) => ({ key: `r${Date.now().toString(36)}${++rowSeq}`, title: '', type: 'GEN', required: true, file: null, ...patch })

// Kept from the moment one is started -- files dropped, a ready-made pack chosen, or an empty list --
// until it is created or discarded. A pack whose title was changed but has no file yet is still work.
const hasContent = (d) => !!d?.mode && Array.isArray(d.rows)
const DRAFT_DAYS = 7          // a draft holds real documents; one nobody has touched for a week is let go

const sizeText = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`)

// Put each file into the empty row it belongs to -- same type first, then a matching title -- and
// give it a new row of its own when nothing fits.
const placeFiles = (rows, files, types) => {
  const next = rows.map(r => ({ ...r }))
  for (const file of files) {
    const title = titleFromFile(file.name)
    const type = suggestType(title, types)
    const low = title.toLowerCase()
    let i = type === 'GEN' ? -1 : next.findIndex(r => !r.file && r.type === type)
    if (i < 0) {
      i = next.findIndex(r => !r.file && r.title
        && (low.includes(r.title.toLowerCase()) || r.title.toLowerCase().includes(low)))
    }
    if (i >= 0) next[i] = { ...next[i], file }
    else next.push(newRow({ file, title, type }))
  }
  return next
}

const uploadInto = (base, itemId, file) => {
  const fd = new FormData()
  fd.append('file', file)
  return api.post(`${base}/items/${itemId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}

function TypeSelect({ value, types, onChange, disabled = false, className = '' }) {
  const sorted = [...types].sort((x, y) => (x.code === 'GEN') - (y.code === 'GEN') || x.name.localeCompare(y.name))
  const known = value === '__title' || types.some(t => t.code === value)
  return (
    <select value={value} disabled={disabled} onChange={e => onChange(e.target.value)}
            className={`rounded-lg border border-gray-300 px-2 py-1.5 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-500 ${className}`}>
      {!known && <option value={value}>{value}</option>}
      {sorted.map(t => <option key={t.code} value={t.code}>{t.name}</option>)}
      <option value="__title">Other — named after the title</option>
    </select>
  )
}

// A drop area that is also a file picker: a <label> around a hidden input, so a click opens the
// dialog natively and a drop hands over the files.
function DropZone({ onFiles, children, className = '' }) {
  const [over, setOver] = useState(false)
  return (
    <label onDragOver={e => { e.preventDefault(); setOver(true) }} onDragLeave={() => setOver(false)}
           onDrop={e => { e.preventDefault(); setOver(false); const f = [...(e.dataTransfer?.files || [])]; if (f.length) onFiles(f) }}
           className={`block cursor-pointer rounded-xl border-2 border-dashed text-center transition-colors ${over
             ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/40'} ${className}`}>
      <input type="file" multiple className="hidden"
             onChange={e => { const f = [...(e.target.files || [])]; e.target.value = ''; if (f.length) onFiles(f) }} />
      {children}
    </label>
  )
}

// ── creating a dossier ─────────────────────────────────────────────────────────

function NewDossier({ projectId, projectCode, projectName, initial, saveState, onDraftChange, onClose, onCreated }) {
  const [presets, setPresets] = useState([])
  const [types, setTypes] = useState([])
  const [canManageTypes, setCanManageTypes] = useState(false)
  const [imported, setImported] = useState(null)   // { id, detail, skipped } after an archive came in with something left out
  const [mode, setMode] = useState(initial?.mode || null)   // null while choosing · 'custom' · a preset's code
  const [title, setTitle] = useState(initial?.title || '')
  const [rows, setRows] = useState(initial?.rows || [])
  const [busy, setBusy] = useState(null)        // progress text while creating
  const [error, setError] = useState(null)
  const [failed, setFailed] = useState(null)    // { id, files } when some uploads did not go through
  const [incoming, setIncoming] = useState(!!initial?.incoming)
  const [receivedFrom, setReceivedFrom] = useState(initial?.receivedFrom || '')

  const loadPresets = () => api.get(`/projects/${projectId}/dossiers/presets`)
    .then(r => { setPresets(r.data?.presets || []); setTypes(r.data?.types || []); setCanManageTypes(!!r.data?.can_manage_types) })
    .catch(e => setError(errText(e, 'Could not load the dossier types.')))
  useEffect(() => { loadPresets() }, [projectId]) // eslint-disable-line react-hooks/exhaustive-deps

  // A saved pack is a template: retiring it stops it being offered, and changes nothing already made.
  const retireType = async (p) => {
    if (!window.confirm(`Stop offering “${p.name}”?\n\nDossiers already started from it are not changed.`)) return
    try { await api.delete(`/templates/${p.id}`); await loadPresets() }
    catch (e) { setError(errText(e, 'It could not be removed.')) }
  }

  // An old archive, all at once: the server unpacks the zip and makes a document of every file in it.
  const importZip = async (file) => {
    setError(null); setBusy(`Importing ${file.name}…`)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const { data } = await api.post(`/projects/${projectId}/dossiers/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      if (data.skipped?.length) setImported(data)
      else onCreated(data.id)
    } catch (e) {
      setError(errText(e, 'The archive could not be imported.'))
    } finally { setBusy(null) }
  }

  // Everything typed or dropped is handed up to the page as it happens, so closing this -- by the X, or
  // by any accident -- loses nothing. The page keeps it, and saves it in the browser, files included.
  useEffect(() => {
    if (mode) onDraftChange?.({ mode, title, rows, incoming, receivedFrom })
  }, [mode, title, rows, incoming, receivedFrom]) // eslint-disable-line react-hooks/exhaustive-deps

  const defaultTitle = `Submission pack${projectName ? ` — ${projectName}` : ''}`
  const startFromFiles = (files) => { setMode('custom'); setTitle(t => t || defaultTitle); setRows(placeFiles([], files, types)) }
  const startFromFolder = (files) => {
    const folder = (files[0]?.webkitRelativePath || '').split('/')[0]
    setMode('custom'); setTitle(t => t || (folder ? `Imported archive — ${folder}` : defaultTitle)); setRows(placeFiles([], files, types))
  }
  const startEmpty = () => { setMode('custom'); setTitle(t => t || defaultTitle); setRows([newRow({})]) }
  const startPreset = (p) => {
    setMode(p.code); setTitle(p.name)
    setRows(p.slots.map(sl => newRow({ title: sl.title, type: sl.code, required: sl.required })))
  }
  const setRow = (key, patch) => setRows(rs => rs.map(r => (r.key === key ? { ...r, ...patch } : r)))
  const startAgain = () => {
    if (rows.some(r => r.file || r.title.trim())
        && !window.confirm('Discard this draft and start again?\n\nEverything you added here is forgotten — nothing has been uploaded yet.')) return
    setMode(null); setRows([]); setTitle(''); setIncoming(false); setReceivedFrom('')
    onDraftChange?.(null)
  }

  const withFiles = rows.filter(r => r.file).length
  const tooBig = rows.some(r => r.file && r.file.size > MAX_BYTES)
  const canCreate = !busy && title.trim() && rows.length > 0 && rows.every(r => r.title.trim())
    && !tooBig && (!incoming || receivedFrom.trim())

  const create = async () => {
    setError(null); setBusy('Creating the dossier…')
    let id = null
    try {
      const { data } = await api.post(`/projects/${projectId}/dossiers/`, {
        title, type_code: mode === 'custom' ? 'custom' : mode,
        slots: rows.map(r => ({ code: effectiveCode(r), title: r.title.trim(), required: r.required })),
        direction: incoming ? 'incoming' : 'outgoing', received_from: receivedFrom,
      })
      id = data.id
      // It exists now, so the draft has done its job. Dropped before the uploads, not after: someone who
      // leaves mid-upload must not come back to a draft that would create the same dossier a second time.
      onDraftChange?.(null)
      const dbase = `/projects/${projectId}/dossiers/${id}`
      const { data: made } = await api.get(dbase)
      const bad = []
      let n = 0
      // One request per file, in slot order: the gateway caps a request at ~50 MB, and a batch that
      // fails as a whole would not say which file was the problem.
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i]
        if (!r.file || !made.items[i]) continue
        n += 1
        setBusy(`Uploading ${n} of ${withFiles} — ${r.file.name}`)
        try { await uploadInto(dbase, made.items[i].id, r.file) } catch (e) { bad.push(`${r.file.name}: ${errText(e, 'upload failed')}`) }
      }
      if (bad.length) { setFailed({ id, files: bad }); setBusy(null); return }
      onCreated(id)
    } catch (e) {
      if (id) setFailed({ id, files: [errText(e, 'The files could not be uploaded.')] })
      else setError(errText(e, 'The dossier could not be created.'))
      setBusy(null)
    }
  }

  if (failed) {
    return (
      <Modal title="Dossier created" onClose={() => onCreated(failed.id)} wide>
        <p className="text-sm text-gray-700">The dossier was created, but {failed.files.length === 1 ? 'one file' : `${failed.files.length} files`} could not be uploaded:</p>
        <ul className="mt-2 text-sm text-red-700 list-disc pl-5">{failed.files.map(f => <li key={f}>{f}</li>)}</ul>
        <p className="text-sm text-gray-600 mt-2">Their slots are ready — upload them from the dossier.</p>
        <div className="flex justify-end mt-4">
          <button onClick={() => onCreated(failed.id)} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium">Open the dossier</button>
        </div>
      </Modal>
    )
  }

  if (imported) {
    return (
      <Modal title="Archive imported" onClose={() => onCreated(imported.id)} wide>
        <p className="text-sm text-gray-700">{imported.detail} These were left out:</p>
        <ul className="mt-2 text-sm text-amber-800 list-disc pl-5 max-h-56 overflow-y-auto">{imported.skipped.map(f => <li key={f}>{f}</li>)}</ul>
        <div className="flex justify-end mt-4">
          <button onClick={() => onCreated(imported.id)} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium">Open the dossier</button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal title="New dossier" onClose={busy ? undefined : onClose} wide>
      {!mode ? (
        <div className="space-y-5">
          <DropZone onFiles={startFromFiles} className="p-8">
            <Upload className="w-8 h-8 mx-auto text-blue-600" />
            <p className="mt-2 text-base font-semibold text-gray-900">Start from your files</p>
            <p className="text-sm text-gray-600 mt-1">Drop every document for this pack here, or click to choose them.</p>
            <p className="text-xs text-gray-500 mt-1">Each one is named from its file name — change anything before you create it.</p>
          </DropZone>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Or start from a ready-made pack</p>
            <div className="grid sm:grid-cols-3 gap-3 mt-2">
              {presets.map(pr => (
                <div key={pr.code} className="relative">
                  <button onClick={() => startPreset(pr)}
                          className="w-full h-full text-left rounded-xl border border-gray-200 p-4 hover:border-blue-400 hover:bg-blue-50/40">
                    <p className="font-medium text-gray-900 pr-5">{pr.name}</p>
                    <p className="text-xs text-gray-500 mt-1">{pr.description}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      {pr.slots.length} documents · kept {pr.retention_years} years{pr.saved ? ' · saved by your team' : ''}
                    </p>
                  </button>
                  {pr.saved && canManageTypes && (
                    <button onClick={() => retireType(pr)} title="Stop offering this pack"
                            className="absolute top-2 right-2 text-gray-300 hover:text-red-600"><X className="w-4 h-4" /></button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Or bring in an old archive</p>
            <p className="text-xs text-gray-500 mt-1">Every file in it becomes a document of its own, named from its file name.</p>
            <div className="flex flex-wrap gap-2 mt-2">
              <label className={`px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 ${busy ? 'opacity-40 pointer-events-none' : 'cursor-pointer'}`}>
                <FileArchive className="w-4 h-4" /> Import a .zip <span className="text-xs text-gray-400">(up to 50 MB)</span>
                <input type="file" accept=".zip,application/zip" className="hidden"
                       onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importZip(f) }} />
              </label>
              <label className={`px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 ${busy ? 'opacity-40 pointer-events-none' : 'cursor-pointer'}`}>
                <FolderUp className="w-4 h-4" /> Choose a folder <span className="text-xs text-gray-400">(any size)</span>
                <input type="file" multiple className="hidden" {...{ webkitdirectory: '' }}
                       onChange={e => { const f = [...(e.target.files || [])].filter(x => !isJunkFile(x)); e.target.value = ''; if (f.length) startFromFolder(f) }} />
              </label>
            </div>
            {busy && <p className="mt-2 text-sm text-gray-600 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {busy}</p>}
          </div>
          <button onClick={startEmpty} className="text-sm text-blue-600 hover:underline">Start with an empty list instead</button>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      ) : (
        <div className="space-y-4">
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Dossier title</span>
            <input value={title} onChange={e => setTitle(e.target.value)}
                   className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                   placeholder="e.g. Final submission to the client" />
          </label>
          <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={incoming} onChange={e => setIncoming(e.target.checked)} />
              <Inbox className="w-4 h-4 text-indigo-600" /> This pack was received from an outside organisation
            </label>
            {incoming && (
              <input value={receivedFrom} onChange={e => setReceivedFrom(e.target.value)}
                     className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                     placeholder="Received from — e.g. a subcontractor's name" />
            )}
          </div>
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs font-medium text-gray-600">Documents in this pack ({rows.length})</span>
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5 text-green-600" /> Named from the file names — nothing is read or sent anywhere
              </span>
            </div>
            <div className="mt-2 rounded-xl border border-gray-200 divide-y divide-gray-100">
              {rows.length === 0 && <p className="p-4 text-sm text-gray-500">No documents yet — add files below.</p>}
              {rows.map(r => (
                <div key={r.key} className="p-3">
                  <div className="flex items-center gap-2">
                    <input value={r.title} onChange={e => setRow(r.key, { title: e.target.value })}
                           className="flex-1 min-w-0 rounded-lg border border-gray-300 px-3 py-1.5 text-sm" placeholder="Document title" />
                    <TypeSelect value={r.type} types={types} onChange={v => setRow(r.key, { type: v })} className="w-56" />
                    <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                      <input type="checkbox" checked={r.required} onChange={e => setRow(r.key, { required: e.target.checked })} /> Required
                    </label>
                    <button onClick={() => setRows(rs => rs.filter(x => x.key !== r.key))}
                            className="text-gray-400 hover:text-red-600" title="Remove"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between gap-3 text-xs">
                    {r.file ? (
                      <span className={`flex items-center gap-1 min-w-0 ${r.file.size > MAX_BYTES ? 'text-red-600' : 'text-gray-600'}`}>
                        <FileText className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{r.file.name}</span>
                        <span className="shrink-0 text-gray-400">· {sizeText(r.file.size)}{r.file.size > MAX_BYTES ? ' — too large, 50 MB at most' : ''}</span>
                        <button onClick={() => setRow(r.key, { file: null })} className="shrink-0 text-gray-400 hover:text-red-600" title="Take this file out">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ) : (
                      <label className="text-blue-600 hover:underline cursor-pointer">
                        No file yet — choose one, or add it later
                        <input type="file" className="hidden"
                               onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) setRow(r.key, { file: f }) }} />
                      </label>
                    )}
                    <span className="font-mono text-gray-400 shrink-0">{projectCode}-{effectiveCode(r)}-…</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 grid sm:grid-cols-2 gap-2">
              <DropZone onFiles={files => setRows(rs => placeFiles(rs, files, types))} className="p-3">
                <span className="text-sm text-gray-700 inline-flex items-center gap-1.5"><Upload className="w-4 h-4" /> Add more files</span>
              </DropZone>
              <button onClick={() => setRows(rs => [...rs, newRow({})])}
                      className="rounded-xl border-2 border-dashed border-gray-300 p-3 text-sm text-gray-700 hover:border-blue-400 hover:bg-blue-50/40 inline-flex items-center justify-center gap-1.5">
                <Plus className="w-4 h-4" /> A document still to come
              </button>
            </div>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          {hasContent({ mode, rows }) && (
            <p className="text-xs text-gray-500 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-green-600 shrink-0" />
              {saveState === 'saved' ? 'Draft saved in this browser. You can close this and carry on later — nothing is lost.'
                : saveState === 'memory' ? 'Kept while this page stays open. This browser cannot save it any longer than that.'
                  : 'Saving this draft…'}
            </p>
          )}
          <div className="flex items-center justify-between pt-1">
            <button onClick={startAgain} disabled={!!busy} className="text-sm text-gray-600 hover:underline">← Discard and start again</button>
            <button onClick={create} disabled={!canCreate}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40 inline-flex items-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {busy || `Create dossier${withFiles ? ` · upload ${withFiles} file${withFiles === 1 ? '' : 's'}` : ''}`}
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}

// ── one dossier ───────────────────────────────────────────────────────────────

// A two-folder dossier shows its Working and Submission folders apart; every other dossier its one list.
const folderGroups = (d) => (d.two_folders
  ? [{ key: 'working', title: 'Working folder', hint: 'Supporting documents. Each is approved here first.',
       items: d.items.filter(i => i.folder !== 'submission') },
     { key: 'submission', title: 'Submission folder', hint: 'What will be sent. Each document is approved once more here; only this folder is submitted.',
       items: d.items.filter(i => i.folder === 'submission') }]
  : [{ key: 'all', title: null, items: d.items }])

function StepChips({ item }) {
  if (item.status === 'Sent back') {
    return (
      <span title={item.sent_back ? `Sent back by ${item.sent_back.by || '—'} · ${formatDateTime(item.sent_back.at)}` : 'Sent back'}
            className="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] border bg-red-50 text-red-700 border-red-200">
        <RotateCcw className="w-3 h-3" /> Sent back
      </span>
    )
  }
  const reached = item.status === 'Missing' ? -1 : STEPS.indexOf(item.status)
  const stamp = { Draft: item.uploaded, Verified: item.verified, Reviewed: item.reviewed, Approved: item.approved }
  return (
    <div className="flex items-center gap-1">
      {STEPS.map((s, i) => {
        const done = i <= reached
        const st = stamp[s]
        return (
          <span key={s} title={st ? `${s} by ${st.by || '—'} · ${formatDateTime(st.at)}` : s}
                className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] border ${done
                  ? (s === 'Approved' ? 'bg-green-600 text-white border-green-600' : 'bg-blue-50 text-blue-700 border-blue-200')
                  : 'bg-white text-gray-400 border-gray-200'}`}>
            {done && <Check className="w-3 h-3" />}{s}
          </span>
        )
      })}
    </div>
  )
}

function DossierDetail({ projectId, dossierId, isArchived, onBack, onDeleted, focusDoc = null, onOpenDossier }) {
  const [d, setD] = useState(null)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [busy, setBusy] = useState(null)
  const [signing, setSigning] = useState(null)      // item being approved
  const [signature, setSignature] = useState('')
  const [onPaper, setOnPaper] = useState(false)     // approving a document that was signed on paper
  const [paper, setPaper] = useState({ signed_by: '', signed_on: '', original_at: '' })
  const [reviewing, setReviewing] = useState(null)  // 'schedule' · 'record'
  const [reviewMonths, setReviewMonths] = useState(12)
  const [reviewNote, setReviewNote] = useState('')
  const [keeping, setKeeping] = useState(null)      // { years, reason } while keeping it longer
  const [savingType, setSavingType] = useState(null) // { name, description } while saving as a ready-made pack
  const [citeOthers, setCiteOthers] = useState([])  // the project's other dossiers, for citing across them
  const [linking, setLinking] = useState(null)      // item being linked to an existing document
  const [projectDocs, setProjectDocs] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [recipient, setRecipient] = useState('')
  const [addingSlot, setAddingSlot] = useState(false)
  const [newSlot, setNewSlot] = useState({ title: '', type: 'GEN', required: true, touched: false })
  const [sharing, setSharing] = useState(false)
  const [shareTo, setShareTo] = useState('')
  const [shareDays, setShareDays] = useState(7)
  const [newLink, setNewLink] = useState(null)
  const [linkCopied, setLinkCopied] = useState(false)    // "Copied" for a moment: a silent copy looked like nothing happened
  const [destroying, setDestroying] = useState(false)
  const [basis, setBasis] = useState('privacy_request')
  const [destroyReason, setDestroyReason] = useState('')
  const [destroyConfirm, setDestroyConfirm] = useState('')
  const [citing, setCiting] = useState(null)        // item whose citations are being edited
  const [citePick, setCitePick] = useState([])
  const [deleting, setDeleting] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [types, setTypes] = useState([])
  const [editing, setEditing] = useState(null)      // { item, title, type, required }
  const [adding, setAdding] = useState(null)        // rows of files being added to this dossier
  const [fixing, setFixing] = useState(null)        // { to, reason } while correcting the recipient
  const [dragOver, setDragOver] = useState(false)
  const [ruling, setRuling] = useState(null)        // the rules being changed, while their window is open
  const [sendForm, setSendForm] = useState(null)    // the Submit window of a company dossier: how it travels
  const [receipting, setReceipting] = useState(null) // { number, reference, file } while recording a delivery
  const [archiving, setArchiving] = useState(null)   // the number typed to confirm the archive
  const [historyOf, setHistoryOf] = useState(null)   // the document whose own track record is open
  const fileRef = useRef(null)
  const uploadTarget = useRef(null)
  const base = `/projects/${projectId}/dossiers/${dossierId}`

  const load = async () => {
    try {
      const { data } = await api.get(base)
      setD(data)
    } catch (e) {
      setError(errText(e, 'Could not load the dossier.'))
    }
  }
  useEffect(() => { setD(null); load() }, [dossierId]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get(`/projects/${projectId}/dossiers/presets`).then(r => setTypes(r.data?.types || [])).catch(() => setTypes([]))
  }, [projectId])
  // Arrived from a citation in another dossier: bring the cited document into view.
  useEffect(() => {
    if (d && focusDoc) document.getElementById(`dossier-item-${focusDoc}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [d?.id, focusDoc]) // eslint-disable-line react-hooks/exhaustive-deps

  const openCiting = async (item) => {
    setNotice(null); setCiting(item); setCitePick(item.references || [])
    try {
      const { data } = await api.get(`/projects/${projectId}/dossiers/citable`, { params: { exclude: dossierId } })
      setCiteOthers(data?.dossiers || [])
    } catch { setCiteOthers([]) }
  }

  const openApprove = (target) => {
    setNotice(null); setSigning(target); setSignature('')
    setOnPaper(false); setPaper({ signed_by: '', signed_on: new Date().toISOString().slice(0, 10), original_at: '' })
  }

  const run = async (key, fn, ok) => {
    setBusy(key); setNotice(null)
    try {
      const r = await fn()
      setNotice({ kind: 'ok', text: ok || r?.data?.detail || 'Done.' })
      await load()
      return true
    } catch (e) {
      setNotice({ kind: 'error', text: errText(e, 'That did not work.') })
      return false
    } finally { setBusy(null) }
  }

  const pickFile = (item) => { uploadTarget.current = item; fileRef.current?.click() }
  const onFile = async (e) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    const item = uploadTarget.current
    if (!f || !item) return
    const fd = new FormData()
    fd.append('file', f)
    await run(`up-${item.id}`, () => api.post(`${base}/items/${item.id}/upload`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }))
  }

  const openLink = async (item) => {
    setNotice(null)
    setLinking(item)
    try {
      const { data } = await api.get(`/projects/${projectId}/documents/`)
      setProjectDocs(data?.documents || [])
    } catch { setProjectDocs([]) }
  }

  const sendBack = async (item) => {
    const reason = window.prompt(`Why is ${item.doc_number} being sent back?\n\nWhoever put it in is told, with this reason, and sends a corrected version.`)
    if (!reason) return
    await run(`sb-${item.id}`, () => api.post(`${base}/items/${item.id}/send-back`, { reason }))
  }

  const moveTo = async (items, to) => {
    let reason
    if (to === 'working') {
      reason = window.prompt('Why is it taken out of the Submission folder?\n\nIt is approved again in the Working folder before it can return.')
      if (!reason) return
    }
    await run(`mv-${to}`, () => (to === 'submission'
      ? api.post(`${base}/agree`, { item_ids: items.map(i => i.id) })
      : api.post(`${base}/move`, { item_ids: items.map(i => i.id), to, reason })))
  }

  const openRules = () => {
    setNotice(null)
    setRuling({ approver_id: d.rules.approver_id || '', backup_id: d.rules.backup_id || '', steps: d.rules.steps,
                move: d.rules.move, send_ways: [...d.rules.send_ways], archivers: [...d.rules.archivers] })
  }

  const revise = async (item) => {
    const reason = window.prompt(`Why does ${item.doc_number} need changing?\n\nThe reason is kept on the record.`)
    if (!reason) return
    await run(`rv-${item.id}`, () => api.post(`${base}/items/${item.id}/revise`, { reason }))
  }

  const download = async (item) => {
    try {
      const r = await api.get(`${base}/items/${item.id}/download`, { responseType: 'blob' })
      saveBlob(r.data, `${item.doc_number}_${item.file_name || 'document'}`)
    } catch { setNotice({ kind: 'error', text: 'Could not download that file.' }) }
  }

  // Open a document in a new tab, whatever its type: PDFs and pictures as they are, Word, Excel and
  // PowerPoint converted on our own server. The tab is opened at the click, before the file
  // arrives, so the browser does not treat it as an unwanted pop-up.
  const openFile = async (item) => {
    const tab = window.open('', '_blank')
    if (tab) tab.document.write('<p style="font-family:Segoe UI,Arial,sans-serif;color:#52514e;padding:24px">Opening the document…</p>')
    try {
      const r = await api.get(`${base}/items/${item.id}/view`, { responseType: 'blob' })
      const url = URL.createObjectURL(r.data)
      if (tab) tab.location.href = url
      else window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 10 * 60_000)
    } catch {
      if (tab) tab.close()
      setNotice({ kind: 'error', text: 'Could not open that file — try Download instead.' })
    }
  }

  // The team link: this dossier inside ProTrack. Anyone on the project can open it once signed in.
  const copyTeamLink = async () => {
    const url = `${window.location.origin}/projects/${projectId}?tab=documents&dossier=${dossierId}`
    try { await navigator.clipboard.writeText(url); setNotice({ kind: 'ok', text: 'Link copied — anyone on this project can open it after signing in.' }) }
    catch { window.prompt('Copy this link:', url) }
  }

  // Exactly what an outside organisation sees, through a 30-minute link only the manager has.
  const previewAsClient = async () => {
    const tab = window.open('', '_blank')
    try {
      const { data: r } = await api.post(`${base}/preview-link`)
      if (tab) tab.location.href = shareUrl(r.token)
    } catch (e) {
      if (tab) tab.close()
      setNotice({ kind: 'error', text: errText(e, 'The preview could not be opened.') })
    }
  }

  const downloadPack = async () => {
    setBusy('pack')
    try {
      const r = await api.get(`${base}/pack`, { responseType: 'blob' })
      saveBlob(r.data, `${d.dossier_number}.zip`)
      await load()
    } catch { setNotice({ kind: 'error', text: 'The pack could not be built.' }) }
    finally { setBusy(null) }
  }

  const downloadTrack = async () => {
    try {
      const r = await api.get(`${base}/track-record`, { responseType: 'blob' })
      saveBlob(r.data, `${d.dossier_number}_track-record.html`)
    } catch { setNotice({ kind: 'error', text: 'The track record could not be downloaded.' }) }
  }

  const removeFile = async (item) => {
    if (!window.confirm(`Take “${item.file_name}” out of ${item.doc_number}?\n\nThe slot stays, ready for another file.`)) return
    await run(`rm-${item.id}`, () => api.delete(`${base}/items/${item.id}/file`))
  }
  const removeSlot = async (item) => {
    if (!window.confirm(`Remove ${item.doc_number} “${item.title}” from this dossier?${item.has_file ? '\n\nIts file is deleted too.' : ''}`)) return
    await run(`rs-${item.id}`, () => api.delete(`${base}/items/${item.id}`))
  }
  const deleteDossier = async () => {
    setBusy('delete'); setNotice(null)
    try {
      const { data } = await api.delete(base, { params: { confirm: deleteConfirm } })
      setDeleting(false)
      onDeleted?.(data.detail)
    } catch (e) {
      setNotice({ kind: 'error', text: errText(e, 'It could not be deleted.') })
    } finally { setBusy(null) }
  }

  // Files added to an existing dossier go into the empty slot they belong to; anything that fits
  // nowhere becomes a new document (managers only) or is left out.
  const openAdd = (files) => {
    setNotice(null)
    const free = d.items.filter(i => i.status === 'Missing')
    const used = new Set()
    setAdding(files.map(file => {
      const title = titleFromFile(file.name)
      const type = suggestType(title, types)
      const low = title.toLowerCase()
      let slot = type === 'GEN' ? null : free.find(i => !used.has(i.id) && i.doc_code === type)
      if (!slot) {
        slot = free.find(i => !used.has(i.id)
          && (low.includes(i.title.toLowerCase()) || i.title.toLowerCase().includes(low)))
      }
      if (slot) used.add(slot.id)
      return newRow({ file, title, type, target: slot ? String(slot.id) : (d.can_shape ? 'new' : 'skip') })
    }))
  }

  const runAdd = async () => {
    setBusy('add')
    const bad = []
    let ok = 0
    for (const r of adding) {
      if (r.target === 'skip') continue
      try {
        let itemId = r.target
        if (r.target === 'new') {
          const { data: made } = await api.post(`${base}/items`,
            { code: effectiveCode(r), title: r.title.trim() || titleFromFile(r.file.name), required: true })
          itemId = made.id
        }
        await uploadInto(base, itemId, r.file)
        ok += 1
      } catch (e) { bad.push(`${r.file.name}: ${errText(e, 'failed')}`) }
    }
    setBusy(null); setAdding(null)
    await load()
    setNotice(bad.length
      ? { kind: 'error', text: `${ok} added. Not added — ${bad.join('; ')}` }
      : { kind: 'ok', text: `${ok} file${ok === 1 ? '' : 's'} added.` })
  }

  const copyLink = async (token) => {
    const url = shareUrl(token)
    try { await navigator.clipboard.writeText(url); setNotice({ kind: 'ok', text: 'Link copied — send it to them.' }) }
    catch { window.prompt('Copy this link:', url) }
  }

  if (error) return <p className="text-sm text-red-600">{error}</p>
  if (!d) return <p className="text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading dossier…</p>

  // A viewer in the team reads the dossier and is offered nothing to change.
  const locked = d.status === 'Submitted' || !!d.destroyed || isArchived || !!d.read_only
  const live = d.can_manage && !d.destroyed && !isArchived     // may share or destroy
  // Approve, send back, move and submit: the dossier's named approver and backup, or the administrator.
  const decides = (d.can_decide ?? d.can_manage) && !locked
  const shape = d.can_shape && !locked      // add, rename and remove documents: whoever started it, and the manager
  const shown = shownStatus(d)
  const pct = d.counts.required ? Math.round((d.counts.approved / d.counts.required) * 100) : 0
  const modalError = notice?.kind === 'error' ? notice.text : null
  const pcode = d.dossier_number.split('-DOS-')[0]
  // A second person checks every document, so what you put in yourself is never offered to you to check.
  const drafts = d.items.filter(i => i.status === 'Draft' && !i.mine)
  const verifiedItems = d.items.filter(i => i.status === 'Verified' && !i.mine)
  const reviewedItems = d.items.filter(i => (d.one_step ? ['Draft', 'Verified', 'Reviewed'].includes(i.status) && i.has_file
    : i.status === 'Reviewed') && !i.mine)
  const freeSlots = d.items.filter(i => i.status === 'Missing')
  const settings = d.can_manage && !d.destroyed && !isArchived   // who sees it, its review, its retention
  const auditExtra = (a) => {
    const x = a.details || {}
    let t = ''
    if (x.changes?.id) t += ` — ID ${x.changes.id[0]} → ${x.changes.id[1]}`
    if (x.changes?.title) t += ` — “${x.changes.title[0]}” → “${x.changes.title[1]}”`
    if (x.changes?.required) t += x.changes.required[1] ? ' — now required' : ' — now optional'
    if (x.changes?.hidden) t += ' — now hidden from the team'
    if (a.action === 'review_schedule_set') t += x.months ? ` — every ${x.months} months` : ' — none'
    else if (a.action === 'retention_extended') t += ` — ${x.was} → ${x.years} years, until ${formatDate(x.until)}`
    else if (x.was) t += ` — was “${x.was}”`
    if (x.paper) t += ` — signed on paper by ${x.paper.by} on ${formatDate(x.paper.on)}`
    if (x.together > 1) t += ` (one of ${x.together} done together)`
    if (x.from) t += ` — from ${x.from}`
    if (x.note) t += ` — “${x.note}”`
    if (x.name) t += ` — “${x.name}”`
    if (x.agreed_by?.length) t += ` — agreed by ${x.agreed_by.join(', ')}`
    if (a.action === 'seal_checked') t += x.intact ? ' — intact' : ' — NOT intact'
    if (a.action === 'rules_changed') {
      t += ' — ' + Object.entries(x.changes || {}).map(([k, v]) => `${RULE_LABEL[k] || k}: ${ruleText(k, v[0])} → ${ruleText(k, v[1])}`).join('; ')
    }
    if (a.action === 'archive_imported') t += ` — ${x.file}: ${x.documents} documents${x.skipped ? `, ${x.skipped} left out` : ''}`
    return t
  }

  return (
    <div className="space-y-4">
      <input ref={fileRef} type="file" className="hidden" onChange={onFile} />
      <button onClick={onBack} className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> All dossiers
      </button>

      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-mono text-gray-500">{d.dossier_number}</p>
            <h3 className="text-lg font-semibold text-gray-900">{d.title}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {d.type_name} · created {formatDate(d.created?.at)}{d.created?.by ? ` by ${d.created.by}` : ''} · kept until {formatDate(d.retain_until)}
              {settings && (
                <button onClick={() => { setNotice(null); setKeeping({ years: d.retention_years + 1, reason: '' }) }}
                        className="ml-1.5 text-blue-600 hover:underline">Keep longer</button>
              )}
            </p>
            {d.hidden && (
              <p className="text-xs text-gray-700 mt-1 flex items-center gap-1">
                <Lock className="w-3.5 h-3.5 text-gray-500" /> Hidden from the team —{' '}
                {d.can_manage ? 'only the project’s managers and whoever started it can see it' : 'you can see it because you started it'}
              </p>
            )}
            {!d.destroyed && (
              <p className="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-1">
                <CalendarClock className="w-3.5 h-3.5" />
                {d.review_every_months
                  ? `Periodic review every ${d.review_every_months} months · next due ${formatDate(d.next_review)}`
                  : 'No periodic review set'}
                {d.last_reviewed_at ? ` · last reviewed ${formatDate(d.last_reviewed_at)}` : ''}
                {settings && (<>
                  <button onClick={() => { setNotice(null); setReviewMonths(d.review_every_months || 12); setReviewing('schedule') }}
                          className="ml-1 text-blue-600 hover:underline">{d.review_every_months ? 'Change' : 'Set one'}</button>
                  <button onClick={() => { setNotice(null); setReviewNote(''); setReviewing('record') }}
                          className="ml-1 text-blue-600 hover:underline">Record a review</button>
                </>)}
              </p>
            )}
            {d.direction === 'incoming' && (
              <p className="text-xs text-indigo-700 mt-1 flex items-center gap-1">
                <Inbox className="w-3.5 h-3.5" /> Received from <b>{d.received_from}</b>
              </p>
            )}
            {d.submitted && (
              <p className="text-xs text-green-700 mt-1 flex items-center gap-1">
                <Send className="w-3.5 h-3.5" />
                {d.direction === 'incoming' ? 'Accepted' : <>Submitted to <b>{d.submitted_to}</b></>} by {d.submitted.by} · {formatDateTime(d.submitted.at)} — locked
                {d.accept_note ? ` · “${d.accept_note}”` : ''}
                {d.direction !== 'incoming' && d.can_manage && !d.destroyed && !isArchived && (
                  <button onClick={() => { setNotice(null); setFixing({ to: d.submitted_to || '', reason: '' }) }}
                          className="ml-1 text-blue-600 hover:underline">Correct recipient</button>
                )}
              </p>
            )}
            {d.closed_reason && (
              <p className="text-xs text-gray-600 mt-1">Closed unsent: “{d.closed_reason}”</p>
            )}
            {d.archived && (
              <p className="text-xs text-slate-700 mt-1 flex items-center gap-1">
                <FolderArchive className="w-3.5 h-3.5" /> Archived and sealed by {d.archived.by} · {formatDateTime(d.archived.at)} — locked
                <button onClick={() => run('seal', () => api.get(`${base}/seal`))} className="ml-1 text-blue-600 hover:underline">Check the seal</button>
              </p>
            )}
            {d.legal_hold && (
              <p className="text-xs text-red-700 mt-1 flex items-center gap-1">
                <Lock className="w-3.5 h-3.5" /> On legal hold since {formatDate(d.legal_hold.at)} ({d.legal_hold.by}): “{d.legal_hold.reason}” —
                nothing in it may be archived, destroyed or deleted
              </p>
            )}
            {d.destroyed && (
              <p className="text-xs text-red-700 mt-1 flex items-center gap-1">
                <Trash2 className="w-3.5 h-3.5" /> Destroyed by {d.destroyed.by} · {formatDateTime(d.destroyed.at)} — only the record remains
              </p>
            )}
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full border ${STATUS_BADGE[shown]}`}>{statusLabel(d)}</span>
        </div>

        {/* Not once destroyed: every document is "Destroyed" then, so it read "0 of 7 approved · 0%" under
            seven signatures. The destruction record tells what happened. */}
        {!d.destroyed && (
          <div className="mt-4">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>{d.counts.approved} of {d.counts.required} required documents approved</span><span>{pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
              <div className={`h-full ${pct === 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={downloadPack} disabled={busy === 'pack'}
                  className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 disabled:opacity-50">
            {busy === 'pack' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderArchive className="w-4 h-4" />} Download all (.zip)
          </button>
          <button onClick={downloadTrack} title="Every arrival, version, approval, send-back, move and submission, with who and when"
                  className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1.5">
            <Download className="w-4 h-4" /> Track record
          </button>
          <button onClick={copyTeamLink}
                  className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1.5">
            <Link2 className="w-4 h-4" /> Copy link
          </button>
          {live && (
            <button onClick={previewAsClient}
                    className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1.5">
              <Eye className="w-4 h-4" /> See it as they will
            </button>
          )}
          {!locked && (
            <label className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 cursor-pointer">
              <Upload className="w-4 h-4" /> Add files
              <input type="file" multiple className="hidden"
                     onChange={e => { const f = [...(e.target.files || [])]; e.target.value = ''; if (f.length) openAdd(f) }} />
            </label>
          )}
          {shape && (
            <button onClick={() => { setNotice(null); setAddingSlot(true) }}
                    className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> Add document slot
            </button>
          )}
          {decides && (
            <button onClick={() => {
                      setNotice(null); setSubmitting(true)
                      setSendForm({ way: d.send_ways?.[0] || 'link', emails: '', days: 30, confirm: true, message: '',
                                    signature: '', reference: '' })
                    }} disabled={!d.ready}
                    title={d.ready ? (d.direction === 'incoming' ? 'Accept the whole pack' : 'Send the whole pack')
                      : 'Every required document must be approved first'}
                    className="px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 flex items-center gap-1.5 disabled:opacity-40">
              <Send className="w-4 h-4" /> {d.direction === 'incoming' ? 'Accept dossier' : 'Submit dossier'}
            </button>
          )}
          {d.can_archive && !isArchived && (
            <button onClick={() => { setNotice(null); setArchiving('') }} disabled={!!d.legal_hold}
                    title={d.legal_hold ? 'On legal hold — lift the hold first' : 'Archive it by hand, sealed'}
                    className="px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-800 hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-40">
              <FolderArchive className="w-4 h-4" /> Archive
            </button>
          )}
          {d.can_close && !isArchived && (
            <button onClick={() => {
                      const reason = window.prompt('Why will it not be sent?\n\nClosing locks it; it can then be archived like one that was sent.')
                      if (reason) run('close', () => api.post(`${base}/close`, { reason }))
                    }}
                    className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50">
              Close unsent
            </button>
          )}
          {d.can_hold && !isArchived && (
            <button onClick={() => {
                      const reason = window.prompt(d.legal_hold ? 'Why is the legal hold lifted?' : 'Why is it put on legal hold?\n\nWhile it stands nothing in this dossier is archived, destroyed or deleted.')
                      if (reason) run('hold', () => api.post(`${base}/legal-hold`, { on: !d.legal_hold, reason }))
                    }}
                    className="px-3 py-2 rounded-lg border border-red-200 text-sm text-red-700 hover:bg-red-50 flex items-center gap-1.5">
              <Lock className="w-4 h-4" /> {d.legal_hold ? 'Lift legal hold' : 'Legal hold'}
            </button>
          )}
          {d.can_reopen && !isArchived && (
            <button onClick={() => {
                      const reason = window.prompt('Why is it reopened?\n\nWhat was sent stays exactly as it was, and its link ends. The next Submit is the next submission.')
                      if (reason) run('reopen', () => api.post(`${base}/reopen`, { reason }))
                    }}
                    className="px-3 py-2 rounded-lg border border-amber-300 text-sm text-amber-800 hover:bg-amber-50 flex items-center gap-1.5">
              <RotateCcw className="w-4 h-4" /> Reopen
            </button>
          )}
          {live && (
            <button onClick={() => { setNotice(null); setSharing(true); setNewLink(null) }}
                    className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1.5">
              <Share2 className="w-4 h-4" /> Share with outside organisation
            </button>
          )}
          {settings && (
            <button onClick={() => run('vis', () => api.post(`${base}/visibility`, { hidden: !d.hidden }))} disabled={busy === 'vis'}
                    title={d.hidden ? 'Let everyone on the project see this dossier' : 'Only the project’s managers, and whoever started it, will see it'}
                    className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 disabled:opacity-50">
              {d.hidden ? <><Eye className="w-4 h-4" /> Show to the team</> : <><EyeOff className="w-4 h-4" /> Hide from the team</>}
            </button>
          )}
          {d.can_save_type && !d.destroyed && (
            <button onClick={() => { setNotice(null); setSavingType({ name: d.title, description: '' }) }}
                    title="Keep this dossier's list of documents as a pack anyone can start from — no files, names or signatures"
                    className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1.5">
              <BookmarkPlus className="w-4 h-4" /> Save as ready-made pack
            </button>
          )}
          {(live || d.can_delete) && !isArchived && (
            <div className="ml-auto flex flex-wrap gap-2">
              {d.can_delete && (
                <button onClick={() => { setNotice(null); setDeleting(true); setDeleteConfirm('') }}
                        title="Throw away a dossier that was never submitted"
                        className="px-3 py-2 rounded-lg text-sm text-red-600 border border-red-200 hover:bg-red-50 flex items-center gap-1.5">
                  <Trash2 className="w-4 h-4" /> Delete dossier
                </button>
              )}
              {live && (
                <button onClick={() => { setNotice(null); setDestroying(true); setDestroyReason(''); setDestroyConfirm('') }}
                        className="px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 flex items-center gap-1.5">
                  <Trash2 className="w-4 h-4" /> Destroy (end of life)
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {!d.destroyed && d.review_due && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 flex flex-wrap items-center gap-3">
          <CalendarClock className="w-5 h-5 text-amber-700 shrink-0" />
          <p className="text-sm text-amber-900 mr-auto">
            Its periodic review was due on <b>{formatDate(d.next_review)}</b>. Check the pack is still right, then record the review.
          </p>
          {settings && (
            <button onClick={() => { setNotice(null); setReviewNote(''); setReviewing('record') }}
                    className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700">Record the review</button>
          )}
        </div>
      )}

      {d.retention_ended && (
        <div className="rounded-xl border border-red-200 bg-red-50/60 px-4 py-3 flex flex-wrap items-center gap-3">
          <Hourglass className="w-5 h-5 text-red-700 shrink-0" />
          <p className="text-sm text-red-900 mr-auto">
            Its retention period ended on <b>{formatDate(d.retain_until)}</b>. Destroy it with a record — or, if it is still needed, keep it longer.
          </p>
          {settings && (<>
            <button onClick={() => { setNotice(null); setKeeping({ years: d.retention_years + 1, reason: '' }) }}
                    className="px-3 py-1.5 rounded-lg border border-red-300 text-sm text-red-800 hover:bg-red-100">Keep longer</button>
            <button onClick={() => { setNotice(null); setBasis('retention_ended'); setDestroying(true); setDestroyReason(''); setDestroyConfirm('') }}
                    className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700">Destroy (end of life)</button>
          </>)}
        </div>
      )}

      {d.destroyed && d.destruction_record && (
        <div className="rounded-xl border border-red-200 bg-red-50/40 p-4">
          <h4 className="text-sm font-semibold text-red-800">Destruction record</h4>
          <p className="text-xs text-red-800 mt-1">
            <b>{d.destruction_record.basis}</b> — {d.destruction_record.reason}<br />
            Retention ran until {formatDate(d.destruction_record.retention_until)}
            {d.destruction_record.early ? ' — destroyed early' : ''}
          </p>
          <table className="mt-2 w-full text-xs">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-1 pr-2">Document</th><th className="py-1 pr-2">File</th>
                <th className="py-1 pr-2">SHA-256 before destruction</th><th className="py-1">Action</th>
              </tr>
            </thead>
            <tbody>
              {d.destruction_record.documents.map((x, i) => (
                <tr key={`${x.doc_number}-${i}`} className="border-t border-red-100 align-top">
                  <td className="py-1 pr-2 font-mono">{x.doc_number} {x.version ? <span className="font-sans text-gray-600">v{x.version}</span> : null}</td>
                  <td className="py-1 pr-2">{x.file || '—'}</td>
                  <td className="py-1 pr-2 font-mono text-[10px] break-all">{x.sha256 || '—'}</td>
                  <td className="py-1">{x.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(d.destruction_record.not_removed || []).length > 0 && (
            <p className="mt-2 text-xs font-medium text-red-700">
              {d.destruction_record.not_removed.length} stored file(s) could not be removed — check storage.
            </p>
          )}
        </div>
      )}

      {d.submissions?.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <h4 className="text-sm font-semibold text-gray-900">Submissions</h4>
          <ul className="mt-2 divide-y divide-gray-100">
            {[...d.submissions].reverse().map(x => (
              <li key={x.number} className="py-2.5 text-xs text-gray-600">
                <div className="flex flex-wrap items-center gap-2">
                  <b className="text-sm text-gray-900">Submission {x.number}</b>
                  <span className={`px-2 py-0.5 rounded-full border ${SUB_BADGE[x.state] || SUB_BADGE.Submitted}`}>{x.state}</span>
                  <span>{WAY_LABEL[x.way]} · to <b>{x.recipient}</b>{x.emails.length ? ` (${x.emails.join(', ')})` : ''}</span>
                </div>
                <p className="mt-0.5">
                  Signed “{x.signature}” · {formatDateTime(x.submitted?.at)} · {x.documents.length} document{x.documents.length === 1 ? '' : 's'}, each with its fingerprint
                  {x.link_expires && ` · link ${x.link_active ? 'works until' : 'ended'} ${formatDate(x.link_expires)}`}
                  {x.opened_at && ` · opened ${formatDateTime(x.opened_at)}`}
                  {x.confirmed_at && ` · receipt confirmed by ${x.confirmed_by} ${formatDateTime(x.confirmed_at)}`}
                  {x.reference && ` · reference ${x.reference}`}
                </p>
                {x.receipt_name && (
                  <button onClick={async () => {
                            try { const r = await api.get(`${base}/submissions/${x.number}/receipt`, { responseType: 'blob' }); saveBlob(r.data, x.receipt_name) }
                            catch (e) { setNotice({ kind: 'error', text: errText(e, 'Could not download the receipt.') }) }
                          }} className="mt-0.5 text-blue-600 hover:underline">Receipt: {x.receipt_name}</button>
                )}
                {x.closed_at && <p className="mt-0.5 text-amber-700">Reopened {formatDateTime(x.closed_at)}: {x.closed_reason}</p>}
                {x.way === 'hand' && !x.receipt_at && !x.closed_at && (d.can_decide ?? d.can_manage) && (
                  <button onClick={() => { setNotice(null); setReceipting({ number: x.number, reference: x.reference || '', file: null }) }}
                          className="mt-1 px-2.5 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
                    Record the reference and receipt
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {d.shares?.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <h4 className="text-sm font-semibold text-gray-900">Shared with outside organisations</h4>
          <ul className="mt-2 divide-y divide-gray-100">
            {d.shares.map(sh => (
              <li key={sh.id} className="py-2 flex flex-wrap items-center justify-between gap-2 text-xs">
                <span className="text-gray-700">
                  <b>{sh.recipient}</b> · {sh.active ? `works until ${formatDateTime(sh.expires_at)}` : sh.revoked ? 'withdrawn' : 'expired'}
                  {' '}· opened {sh.opened}× · downloaded {sh.downloaded}×
                  {sh.last_opened_at ? ` · last opened ${formatDateTime(sh.last_opened_at)}` : ''}
                </span>
                {sh.active && d.can_manage && (
                  <span className="flex gap-3">
                    {sh.token && (
                      <button onClick={() => copyLink(sh.token)} className="text-blue-600 hover:underline flex items-center gap-1">
                        <Copy className="w-3.5 h-3.5" /> Copy link
                      </button>
                    )}
                    <button onClick={() => run(`sh-${sh.id}`, () => api.post(`${base}/shares/${sh.id}/revoke`))}
                            className="text-red-600 hover:underline">Withdraw</button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {notice && (
        <div className={`rounded-lg border px-4 py-2.5 text-sm flex items-center justify-between ${notice.kind === 'error'
          ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-800'}`}>
          {notice.text}
          <button onClick={() => setNotice(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      {d.rules && (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-gray-900">Rules of this dossier</h4>
            {d.can_set_rules && !d.destroyed && !isArchived && (
              <button onClick={openRules} className="text-xs text-blue-600 hover:underline">Change rules</button>
            )}
          </div>
          <dl className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
            {[['Approver', d.rules.approver || ruleText('approver_id', null)],
              ['Backup', d.rules.backup || ruleText('backup_id', null)],
              [RULE_LABEL.steps, ruleText('steps', d.rules.steps)],
              [RULE_LABEL.move, ruleText('move', d.rules.move)],
              [RULE_LABEL.send_ways, ruleText('send_ways', d.rules.send_ways)],
              [RULE_LABEL.archivers, d.rules.archiver_names?.length ? d.rules.archiver_names.join(', ') : ruleText('archivers', null)],
              ['A reason for every send-back', 'Always']].map(([k, v]) => (
              <div key={k} className="flex gap-2"><dt className="text-gray-500 w-44 shrink-0">{k}</dt><dd className="text-gray-800">{v}</dd></div>
            ))}
          </dl>
        </div>
      )}

      {!locked && ((drafts.length > 1 && !d.one_step) || (decides && ((verifiedItems.length > 1 && !d.one_step) || reviewedItems.length > 1))) && (
        <div className="rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-3 flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-700 mr-auto flex items-center gap-1.5">
            <CheckCheck className="w-4 h-4 text-blue-600" /> Save clicks — do a step for all of them at once
          </span>
          {drafts.length > 1 && !d.one_step && (
            <button disabled={busy === 'bulk'}
                    onClick={() => run('bulk', () => api.post(`${base}/steps`, { action: 'verify', item_ids: drafts.map(i => i.id) }))}
                    className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-40">
              Verify all {drafts.length}
            </button>
          )}
          {decides && verifiedItems.length > 1 && !d.one_step && (
            <button disabled={busy === 'bulk'}
                    onClick={() => run('bulk', () => api.post(`${base}/steps`, { action: 'review', item_ids: verifiedItems.map(i => i.id) }))}
                    className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-40">
              Mark all {verifiedItems.length} reviewed
            </button>
          )}
          {decides && reviewedItems.length > 1 && (
            <button onClick={() => openApprove({ bulk: reviewedItems })}
                    className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm hover:bg-green-700 flex items-center gap-1">
              <PenLine className="w-4 h-4" /> Approve & sign all {reviewedItems.length}
            </button>
          )}
        </div>
      )}

      {d.hidden_count > 0 && (
        <p className="text-xs text-gray-500 flex items-center gap-1.5">
          <Lock className="w-3.5 h-3.5" /> {d.hidden_count} more document{d.hidden_count === 1 ? ' is' : 's are'} in this
          dossier, visible only to the project’s managers.
        </p>
      )}

      <div onDragOver={e => { if (!locked) { e.preventDefault(); setDragOver(true) } }}
           onDragLeave={() => setDragOver(false)}
           onDrop={e => { if (locked) return; e.preventDefault(); setDragOver(false); const f = [...(e.dataTransfer?.files || [])]; if (f.length) openAdd(f) }}
           className={`rounded-xl border bg-white divide-y divide-gray-100 ${dragOver ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'}`}>
        {dragOver && <p className="p-3 text-sm text-blue-700 text-center">Drop to add these files to the dossier</p>}
        {folderGroups(d).map(g => (<div key={g.key} className="divide-y divide-gray-100">
          {g.title && (() => {
            // What this person can still agree to move: approved, and not already agreed by them.
            const movable = g.key === 'working' ? g.items.filter(i => i.status === 'Approved' && !i.proposal?.mine) : []
            const alone = d.rules?.move === 'approver'
            return (
              <div className={`px-4 py-2.5 flex flex-wrap items-center gap-2 ${g.key === 'submission' ? 'bg-green-50/70' : 'bg-blue-50/60'}`}>
                <span className="mr-auto">
                  <span className={`text-sm font-semibold ${g.key === 'submission' ? 'text-green-800' : 'text-blue-800'}`}>
                    {g.title} <span className="font-normal text-gray-500">· {g.items.length}</span></span>
                  <span className="block text-xs text-gray-500">{g.hint}</span>
                </span>
                {!locked && d.can_agree && movable.length > 0 && (
                  <button onClick={() => moveTo(movable, 'submission')} disabled={busy === 'mv-submission'}
                          className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs hover:bg-green-700 disabled:opacity-40">
                    {alone ? 'Move' : movable.some(i => i.proposal) ? 'Agree to move' : 'Propose'} {movable.length} approved
                    {alone ? ' to' : ' for'} the Submission folder
                  </button>
                )}
              </div>
            )
          })()}
          {g.title && g.items.length === 0 && (
            <p className="px-4 py-3 text-xs text-gray-400">{g.key === 'submission'
              ? 'Nothing here yet. Approved documents are moved here when the team agrees.' : 'Nothing here.'}</p>
          )}
          {g.items.map(it => (
          <div key={it.id} id={`dossier-item-${it.doc_number}`} className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-mono text-gray-500">{it.doc_number}{it.version > 0 ? ` · v${it.version}` : ''}</p>
                <p className="font-medium text-gray-900 flex items-center gap-1.5">
                  {it.title}{!it.required && <span className="text-xs font-normal text-gray-400">optional</span>}
                  {it.editable && d.can_shape && !locked && (
                    <button onClick={() => { setNotice(null); setEditing({ item: it, title: it.title, type: it.doc_code, required: it.required }) }}
                            className="text-gray-400 hover:text-blue-600" title="Correct the title or type">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {['Missing', 'Draft'].includes(it.status) && d.can_shape && !locked && d.items.length > 1 && (
                    <button onClick={() => removeSlot(it)} className="text-gray-400 hover:text-red-600"
                            title="Remove this document from the dossier">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {it.hidden && (
                    <span title="Only the project's managers, and whoever put its file in, can see it"
                          className="inline-flex items-center gap-0.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-normal text-gray-600">
                      <Lock className="w-3 h-3" /> Hidden from the team
                    </span>
                  )}
                  {settings && (
                    <button onClick={() => run(`vis-${it.id}`, () => api.post(`${base}/items/${it.id}/visibility`, { hidden: !it.hidden }))}
                            disabled={busy === `vis-${it.id}`} className="text-gray-400 hover:text-blue-600 disabled:opacity-40"
                            title={it.hidden ? 'Hidden from the team — click to show it' : 'Visible to the team — click to hide it'}>
                      {/* The icon shows the STATE, like the label beside it: open eye = the team sees it, crossed = hidden. */}
                      {it.hidden ? <EyeOff className="w-3.5 h-3.5 text-gray-600" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </p>
                {it.has_file ? (
                  <div className="flex items-center gap-3 mt-0.5 text-xs">
                    <button onClick={() => openFile(it)} title="Open in a new tab"
                            className="text-blue-600 hover:underline flex items-center gap-1 min-w-0">
                      <FileText className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{it.file_name}</span>
                      <ExternalLink className="w-3 h-3 shrink-0" />
                    </button>
                    <button onClick={() => download(it)} title="Download the original"
                            className="text-gray-500 hover:text-blue-600 flex items-center gap-1 shrink-0">
                      <Download className="w-3.5 h-3.5" /> Download
                    </button>
                  </div>
                ) : <p className="text-xs text-red-600 mt-0.5">{it.status === 'Destroyed' ? 'Destroyed at end of life — see the destruction record' : 'Missing — no document yet'}</p>}
                {it.signature && (
                  <p className="text-xs text-green-700 mt-1 flex items-center gap-1">
                    <ShieldCheck className="w-3.5 h-3.5 shrink-0" /> {signedText(it)} · {formatDateTime(it.approved?.at)}
                  </p>
                )}
                {it.sent_back && (
                  <p className="text-xs text-red-700 mt-1">Sent back by {it.sent_back.by || '—'}: “{it.sent_back.reason}” — send a corrected version.</p>
                )}
                {it.version > 0 && (
                  <button onClick={() => setHistoryOf(historyOf === it.id ? null : it.id)}
                          className="text-xs text-gray-500 hover:text-blue-600 mt-1">
                    {historyOf === it.id ? 'Hide its history' : 'Its history'}
                  </button>
                )}
                {historyOf === it.id && (
                  <ul className="mt-1 border-l-2 border-gray-200 pl-3 space-y-0.5">
                    {d.audit.filter(a => a.details?.doc_number === it.doc_number).map((a, i) => (
                      <li key={i} className="text-xs text-gray-600">
                        <span className="text-gray-400">{formatDateTime(a.at)}</span>{' '}
                        <b className="text-gray-800">{ACTION_LABEL[a.action] || a.action}</b>
                        {a.details?.version ? ` v${a.details.version}` : ''}
                        {a.details?.reason ? ` — “${a.details.reason}”` : ''}
                        {a.details?.signature ? ` — signed “${a.details.signature}”` : ''}
                        {a.details?.agreed_by?.length ? ` — agreed by ${a.details.agreed_by.join(', ')}` : ''}
                        {a.details?.via === 'email' ? ' — by email' : ''}
                        {a.by ? ` · ${a.by}` : ''}
                      </li>
                    ))}
                  </ul>
                )}
                {it.proposal && (
                  <p className="text-xs text-green-800 mt-1">
                    Proposed for the Submission folder by {it.proposal.by || '—'} · agreed: {it.proposal.agreed.join(', ') || '—'}
                    {it.proposal.waiting.length > 0 && <span className="text-gray-500"> · waiting for {it.proposal.waiting.join(', ')}</span>}
                  </p>
                )}
                {it.last_reason && it.status !== 'Approved' && (
                  <p className="text-xs text-amber-700 mt-1">Revised for cause: {it.last_reason}</p>
                )}
                {it.references?.length > 0 && (
                  <p className="text-xs text-gray-600 mt-1 flex flex-wrap items-center gap-1">
                    Cites:
                    {it.references.map(r => {
                      const there = d.cites?.[r]
                      if (d.items.some(x => x.doc_number === r)) {
                        return <a key={r} href={`#dossier-item-${r}`}
                                  className="font-mono text-[11px] text-blue-700 bg-blue-50 border border-blue-100 rounded px-1.5 py-0.5 hover:underline">{r}</a>
                      }
                      if (there) {            // in another of the project's dossiers: opens it there
                        return (
                          <button key={r} onClick={() => onOpenDossier?.(there.dossier_id, r)} title={`${there.title} — in ${there.dossier_number}`}
                                  className="font-mono text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-1.5 py-0.5 hover:underline">
                            {r} <span className="font-sans text-indigo-500">· in {there.dossier_number}</span>
                          </button>
                        )
                      }
                      return <span key={r} title="Not a document you can open"
                                   className="font-mono text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">{r}</span>
                    })}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                {!d.destroyed && <StepChips item={it} />}
                {!locked && (
                  <div className="flex flex-wrap justify-end gap-1.5">
                    {['Missing', 'Draft', 'Sent back'].includes(it.status) && (<>
                      <button onClick={() => pickFile(it)} disabled={busy === `up-${it.id}`}
                              className="px-2.5 py-1 rounded-lg border border-gray-300 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-1">
                        {busy === `up-${it.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        {it.status === 'Sent back' ? 'Send new version' : it.has_file ? 'Replace' : 'Upload'}
                      </button>
                      <button onClick={() => openLink(it)}
                              className="px-2.5 py-1 rounded-lg border border-gray-300 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-1">
                        <Link2 className="w-3.5 h-3.5" /> Use existing
                      </button>
                      <button onClick={() => openCiting(it)}
                              className="px-2.5 py-1 rounded-lg border border-gray-300 text-xs text-gray-700 hover:bg-gray-50">
                        Cites…
                      </button>
                      {it.has_file && it.status === 'Draft' && (
                        <button onClick={() => removeFile(it)} disabled={busy === `rm-${it.id}`}
                                title="Take the file out; the slot stays"
                                className="px-2.5 py-1 rounded-lg text-xs text-red-600 hover:bg-red-50 flex items-center gap-1 disabled:opacity-40">
                          <Trash2 className="w-3.5 h-3.5" /> Remove file
                        </button>
                      )}
                    </>)}
                    {it.status === 'Draft' && !it.mine && !d.one_step && (
                      <button onClick={() => run(`st-${it.id}`, () => api.post(`${base}/items/${it.id}/step`, { action: 'verify' }))}
                              className="px-2.5 py-1 rounded-lg bg-blue-600 text-white text-xs hover:bg-blue-700">Verify</button>
                    )}
                    {it.status === 'Verified' && decides && !it.mine && !d.one_step && (
                      <button onClick={() => run(`st-${it.id}`, () => api.post(`${base}/items/${it.id}/step`, { action: 'review' }))}
                              className="px-2.5 py-1 rounded-lg bg-blue-600 text-white text-xs hover:bg-blue-700">Mark reviewed</button>
                    )}
                    {(it.status === 'Reviewed' || (d.one_step && it.has_file && ['Draft', 'Verified'].includes(it.status)))
                      && decides && !it.mine && (
                      <button onClick={() => openApprove(it)}
                              className="px-2.5 py-1 rounded-lg bg-green-600 text-white text-xs hover:bg-green-700 flex items-center gap-1">
                        <PenLine className="w-3.5 h-3.5" /> Approve & sign
                      </button>
                    )}
                    {it.has_file && ['Draft', 'Verified', 'Reviewed'].includes(it.status) && decides && (
                      <button onClick={() => sendBack(it)} disabled={busy === `sb-${it.id}`}
                              className="px-2.5 py-1 rounded-lg border border-red-300 text-xs text-red-700 hover:bg-red-50 disabled:opacity-40">
                        Send back
                      </button>
                    )}
                    {d.two_folders && d.can_agree && it.status === 'Approved' && it.folder !== 'submission' && !it.proposal?.mine && (
                      <button onClick={() => moveTo([it], 'submission')} disabled={busy === 'mv-submission'}
                              className="px-2.5 py-1 rounded-lg border border-green-300 text-xs text-green-700 hover:bg-green-50 disabled:opacity-40">
                        {d.rules?.move === 'approver' ? 'Move to Submission folder' : it.proposal ? 'Agree to move' : 'Propose for Submission folder'}
                      </button>
                    )}
                    {d.two_folders && decides && it.folder === 'submission' && (
                      <button onClick={() => moveTo([it], 'working')} disabled={busy === 'mv-working'}
                              className="px-2.5 py-1 rounded-lg text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40">
                        Take back
                      </button>
                    )}
                    {it.mine && ['Draft', 'Verified', 'Reviewed'].includes(it.status) && (
                      <span className="px-1 py-1 text-[11px] italic text-gray-500" title="Every document is checked by a second person">
                        You added this file — someone else checks it
                      </span>
                    )}
                    {['Verified', 'Reviewed', 'Approved'].includes(it.status) && d.can_manage && (
                      <button onClick={() => revise(it)}
                              className="px-2.5 py-1 rounded-lg text-xs text-amber-700 hover:bg-amber-50 flex items-center gap-1">
                        <RotateCcw className="w-3.5 h-3.5" /> Revise (for cause)
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}</div>))}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <h4 className="text-sm font-semibold text-gray-900">Audit trail</h4>
        <ul className="mt-2 space-y-1.5 max-h-72 overflow-y-auto">
          {d.audit.map((a, i) => (
            <li key={i} className="text-xs text-gray-600 flex gap-2">
              <span className="text-gray-400 whitespace-nowrap">{formatDateTime(a.at)}</span>
              <span>
                <b className="text-gray-800">{ACTION_LABEL[a.action] || a.action}</b>
                {a.details?.doc_number ? ` · ${a.details.doc_number}` : ''}
                {a.details?.version ? ` v${a.details.version}` : ''}
                {a.details?.reason ? ` — “${a.details.reason}”` : ''}
                {a.details?.to ? ` — to ${a.details.to}` : ''}
                {a.details?.by ? ` — by ${a.details.by}` : ''}
                {a.details?.basis ? ` — ${a.details.basis}` : ''}
                {auditExtra(a)}
                {a.by ? ` · ${a.by}` : ''}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {signing && (
        <Modal error={modalError} title={signing.bulk ? `Approve ${signing.bulk.length} documents` : `Approve ${signing.doc_number}`}
               onClose={() => setSigning(null)}>
          {signing.bulk ? (<>
            <p className="text-sm text-gray-600">
              By typing your name you sign that each of these documents is approved for this dossier. Each one is
              recorded separately, with your signature, the date and the time.
            </p>
            <ul className="mt-3 rounded-lg border border-gray-200 divide-y divide-gray-100 max-h-56 overflow-y-auto">
              {signing.bulk.map(i => (
                <li key={i.id} className="px-3 py-1.5 text-sm text-gray-800">
                  <span className="font-mono text-xs text-gray-500 mr-2">{i.doc_number}</span>{i.title}
                  <span className="text-gray-400"> · v{i.version}</span>
                </li>
              ))}
            </ul>
          </>) : (
            <p className="text-sm text-gray-600">
              By typing your name you sign that <b>{signing.title}</b> (v{signing.version}) is approved for this dossier.
              The signature, date and time are recorded.
            </p>
          )}
          <div className="mt-4 rounded-lg border border-gray-200 p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="radio" checked={!onPaper} onChange={() => setOnPaper(false)} /> Signed here, electronically
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="radio" checked={onPaper} onChange={() => setOnPaper(true)} /> Signed on paper — record who signed the original
            </label>
            {onPaper && (
              <div className="grid sm:grid-cols-2 gap-2 pl-6">
                <label className="block">
                  <span className="text-xs text-gray-600">Signed on paper by</span>
                  <input value={paper.signed_by} onChange={e => setPaper(p => ({ ...p, signed_by: e.target.value }))}
                         className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm" placeholder="e.g. the client's director" />
                </label>
                <label className="block">
                  <span className="text-xs text-gray-600">Date it was signed</span>
                  <input type="date" value={paper.signed_on} max={new Date().toISOString().slice(0, 10)}
                         onChange={e => setPaper(p => ({ ...p, signed_on: e.target.value }))}
                         className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-xs text-gray-600">Where the signed original is kept (optional)</span>
                  <input value={paper.original_at} onChange={e => setPaper(p => ({ ...p, original_at: e.target.value }))}
                         className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm" placeholder="e.g. Geneva office, contracts binder 3" />
                </label>
              </div>
            )}
          </div>
          <label className="block mt-4">
            <span className="text-xs font-medium text-gray-600">
              {onPaper ? 'To record it, type your full name' : 'Type your full name'}: <b>{d.me}</b>
            </span>
            <input autoFocus value={signature} onChange={e => setSignature(e.target.value)}
                   className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm italic" placeholder={d.me} />
          </label>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setSigning(null)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
            <button disabled={!signature.trim() || busy === 'sign' || (onPaper && (!paper.signed_by.trim() || !paper.signed_on))}
                    onClick={async () => {
                      const extra = onPaper ? { paper } : {}
                      if (await run('sign', () => (signing.bulk
                        ? api.post(`${base}/steps`, { action: 'approve', item_ids: signing.bulk.map(i => i.id), signature, ...extra })
                        : api.post(`${base}/items/${signing.id}/step`, { action: 'approve', signature, ...extra }))))
                        setSigning(null)
                    }}
                    className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-40">
              {signing.bulk ? `Sign and approve all ${signing.bulk.length}` : 'Sign and approve'}
            </button>
          </div>
        </Modal>
      )}

      {linking && (
        <Modal error={modalError} title={`Use an existing document for ${linking.doc_number}`} onClose={() => setLinking(null)}>
          {projectDocs.length === 0 ? (
            <p className="text-sm text-gray-500">This project has no documents yet — upload a file instead.</p>
          ) : (
            <div className="space-y-1.5 max-h-80 overflow-y-auto">
              {projectDocs.map(doc => (
                <button key={doc.id}
                        onClick={async () => {
                          if (await run(`ln-${linking.id}`, () => api.post(`${base}/items/${linking.id}/link`, { document_id: doc.id })))
                            setLinking(null)
                        }}
                        className="w-full text-left rounded-lg border border-gray-200 px-3 py-2 hover:bg-blue-50/50">
                  <p className="text-sm text-gray-900">{doc.title}</p>
                  <p className="text-xs text-gray-500">{doc.latest?.original_filename || ''}{doc.latest?.version ? ` · v${doc.latest.version}` : ''}</p>
                </button>
              ))}
            </div>
          )}
        </Modal>
      )}

      {submitting && sendForm && d.two_folders && d.direction !== 'incoming' && (
        <Modal error={modalError} title={`Submit ${d.dossier_number}`} onClose={() => setSubmitting(false)} wide>
          <p className="text-sm text-gray-600">
            <b>What goes:</b> the {d.counts.in_submission} documents in the Submission folder, all approved, with an index and a
            fingerprint for each file. The Working folder stays here. The dossier locks the moment it goes.
          </p>
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600">To (who receives it)</span>
            <input autoFocus value={recipient} onChange={e => setRecipient(e.target.value)}
                   className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="e.g. The regulator — submissions office" />
          </label>
          <fieldset className="mt-3">
            <legend className="text-xs font-medium text-gray-600">How it travels</legend>
            {(d.send_ways || ['link']).map(w => (
              <label key={w} className={`mt-1.5 flex gap-2 rounded-lg border px-3 py-2 cursor-pointer ${sendForm.way === w ? 'border-blue-400 bg-blue-50/50' : 'border-gray-200'}`}>
                <input type="radio" name="way" checked={sendForm.way === w} onChange={() => setSendForm(f => ({ ...f, way: w }))} className="mt-0.5" />
                <span><span className="text-sm text-gray-900">{WAY_LABEL[w]}{w === 'link' && <span className="ml-1.5 text-[11px] text-green-700">recommended</span>}</span>
                  <span className="block text-xs text-gray-500">{WAY_HINT[w]}</span></span>
              </label>
            ))}
          </fieldset>
          {sendForm.way !== 'hand' ? (
            <label className="block mt-3">
              <span className="text-xs font-medium text-gray-600">Their email address(es), separated by commas</span>
              <input value={sendForm.emails} onChange={e => setSendForm(f => ({ ...f, emails: e.target.value }))}
                     className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="submissions@regulator.example" />
            </label>
          ) : (
            <label className="block mt-3">
              <span className="text-xs font-medium text-gray-600">Reference (optional — you can add it later with the receipt)</span>
              <input value={sendForm.reference} onChange={e => setSendForm(f => ({ ...f, reference: e.target.value }))}
                     className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="e.g. the portal's reference number" />
            </label>
          )}
          {sendForm.way === 'link' && (
            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-gray-700">
              <label className="flex items-center gap-2">Link works for
                <select value={sendForm.days} onChange={e => setSendForm(f => ({ ...f, days: Number(e.target.value) }))}
                        className="rounded-lg border border-gray-300 px-2 py-1 text-sm">
                  {[7, 14, 30, 60, 90].map(n => <option key={n} value={n}>{n} days</option>)}
                </select>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={sendForm.confirm} onChange={e => setSendForm(f => ({ ...f, confirm: e.target.checked }))} />
                Ask them to confirm receipt
              </label>
            </div>
          )}
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600">Message (optional)</span>
            <textarea value={sendForm.message} onChange={e => setSendForm(f => ({ ...f, message: e.target.value }))} rows={3}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Please find our annual file through the link below." />
          </label>
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600">Sign — type your full name ({d.me})</span>
            <input value={sendForm.signature} onChange={e => setSendForm(f => ({ ...f, signature: e.target.value }))}
                   className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          </label>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setSubmitting(false)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
            <button disabled={!recipient.trim() || !sendForm.signature.trim() || (sendForm.way !== 'hand' && !sendForm.emails.trim()) || busy === 'submit'}
                    onClick={async () => {
                      const body = { submitted_to: recipient, way: sendForm.way, signature: sendForm.signature, message: sendForm.message,
                                     emails: sendForm.way === 'hand' ? [] : sendForm.emails.split(/[,;\s]+/).filter(Boolean),
                                     days: sendForm.days, confirm_receipt: sendForm.confirm, reference: sendForm.reference }
                      if (await run('submit', () => api.post(`${base}/submit`, body))) setSubmitting(false)
                    }}
                    className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-40">
              {sendForm.way === 'hand' ? 'Submit' : 'Submit and send'}
            </button>
          </div>
        </Modal>
      )}

      {archiving !== null && (
        <Modal error={modalError} title={`Archive ${d.dossier_number}`} onClose={() => setArchiving(null)}>
          <p className="text-sm text-gray-600">
            Archiving locks the dossier for good and seals it: every file is fingerprinted now, so anyone can check later that
            nothing changed. Nothing is deleted. It is kept {d.retention_years} years, until {formatDate(d.retain_until)}; when that
            ends you are reminded, and destruction stays the administrator's decision.
          </p>
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600">Type the dossier number to confirm ({d.dossier_number})</span>
            <input autoFocus value={archiving} onChange={e => setArchiving(e.target.value)}
                   className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono" />
          </label>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setArchiving(null)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
            <button disabled={archiving.trim() !== d.dossier_number || busy === 'archive'}
                    onClick={async () => { if (await run('archive', () => api.post(`${base}/archive`, { confirm: archiving.trim() }))) setArchiving(null) }}
                    className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-40">
              Archive and seal
            </button>
          </div>
        </Modal>
      )}

      {receipting && (
        <Modal error={modalError} title={`Delivery of Submission ${receipting.number}`} onClose={() => setReceipting(null)}>
          <p className="text-sm text-gray-600">The proof it arrived: the reference their portal or the courier gave, and the receipt.</p>
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600">Reference</span>
            <input value={receipting.reference} onChange={e => setReceipting(r => ({ ...r, reference: e.target.value }))}
                   className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          </label>
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600">Receipt (PDF or picture)</span>
            <input type="file" onChange={e => setReceipting(r => ({ ...r, file: e.target.files?.[0] || null }))} className="mt-1 block text-sm" />
          </label>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setReceipting(null)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
            <button disabled={(!receipting.reference.trim() && !receipting.file) || busy === 'receipt'}
                    onClick={async () => {
                      const fd = new FormData()
                      fd.append('reference', receipting.reference)
                      if (receipting.file) fd.append('file', receipting.file)
                      if (await run('receipt', () => api.post(`${base}/submissions/${receipting.number}/receipt`, fd,
                        { headers: { 'Content-Type': 'multipart/form-data' } }))) setReceipting(null)
                    }}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40">
              Record
            </button>
          </div>
        </Modal>
      )}

      {submitting && !(sendForm && d.two_folders && d.direction !== 'incoming') && (
        <Modal error={modalError} title={`${d.direction === 'incoming' ? 'Accept' : 'Submit'} ${d.dossier_number}`} onClose={() => setSubmitting(false)}>
          <p className="text-sm text-gray-600">
            All {d.counts.required} required documents are approved. {d.direction === 'incoming'
              ? 'Accepting records that the pack was checked and approved, and locks it.'
              : 'Submitting records who the pack went to and locks it.'}
          </p>
          <label className="block mt-4">
            <span className="text-xs font-medium text-gray-600">{d.direction === 'incoming' ? 'Note (optional)' : 'Submitted to'}</span>
            <input autoFocus value={recipient} onChange={e => setRecipient(e.target.value)}
                   className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                   placeholder={d.direction === 'incoming' ? 'e.g. Checked against the purchase order'
                     : 'e.g. UNICEF Ethiopia — Programme Officer'} />
          </label>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setSubmitting(false)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
            <button disabled={(!recipient.trim() && d.direction !== 'incoming') || busy === 'submit'}
                    onClick={async () => {
                      if (await run('submit', () => api.post(`${base}/submit`, { submitted_to: recipient })))
                        setSubmitting(false)
                    }}
                    className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-40">
              {d.direction === 'incoming' ? 'Accept dossier' : 'Submit dossier'}
            </button>
          </div>
        </Modal>
      )}

      {sharing && (
        <Modal error={modalError} title={`Share ${d.dossier_number} with an outside organisation`}
               onClose={() => { setSharing(false); setNewLink(null) }}>
          {!newLink ? (<>
            <p className="text-sm text-gray-600">
              They get a link to download the whole pack — no account needed. It stops working when it
              expires or when you withdraw it, and every download is recorded.
            </p>
            <label className="block mt-4">
              <span className="text-xs font-medium text-gray-600">Organisation or person</span>
              <input autoFocus value={shareTo} onChange={e => setShareTo(e.target.value)}
                     className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                     placeholder="e.g. Ministry of Health — Regulatory Unit" />
            </label>
            <label className="block mt-3">
              <span className="text-xs font-medium text-gray-600">Link works for</span>
              <select value={shareDays} onChange={e => setShareDays(Number(e.target.value))}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                {[1, 7, 14, 30].map(n => <option key={n} value={n}>{n} day{n > 1 ? 's' : ''}</option>)}
              </select>
            </label>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setSharing(false)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
              <button disabled={!shareTo.trim() || busy === 'share'}
                      onClick={async () => {
                        setBusy('share')
                        try {
                          const { data: r } = await api.post(`${base}/shares`, { recipient: shareTo, days: shareDays })
                          setNewLink(shareUrl(r.token))
                          await load()
                        } catch (e) {
                          setNotice({ kind: 'error', text: errText(e, 'The link could not be created.') })
                          setSharing(false)
                        } finally { setBusy(null) }
                      }}
                      className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40">
                Create link
              </button>
            </div>
          </>) : (<>
            <p className="text-sm text-gray-600">Send this link to <b>{shareTo}</b>:</p>
            <div className="mt-2 flex gap-2">
              <input readOnly value={newLink} onFocus={e => e.target.select()}
                     className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-xs font-mono" />
              <button onClick={async () => {
                        try { await navigator.clipboard.writeText(newLink); setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2500) }
                        catch { window.prompt('Copy this link:', newLink) }
                      }}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-sm flex items-center gap-1">
                {linkCopied ? <><Check className="w-4 h-4 text-green-600" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
              </button>
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => { setSharing(false); setNewLink(null); setShareTo('') }}
                      className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm">Done</button>
            </div>
          </>)}
        </Modal>
      )}

      {destroying && (
        <Modal error={modalError} title={`Destroy ${d.dossier_number}`} onClose={() => setDestroying(false)}>
          <p className="text-sm text-gray-600">
            End of life: every file this dossier holds is permanently destroyed. A destruction record — who,
            when, why, and a fingerprint of each file — is kept for good. Documents used from the project's
            own documents are only unlinked.
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Kept until {formatDate(d.retain_until)}. Destroying earlier needs a privacy request or a legal instruction.
          </p>
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600">Basis</span>
            <select value={basis} onChange={e => setBasis(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              <option value="retention_ended">Retention period ended</option>
              <option value="privacy_request">Privacy request (right to erasure)</option>
              <option value="legal_instruction">Legal or regulatory instruction</option>
            </select>
          </label>
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600">Reason (kept in the record)</span>
            <textarea value={destroyReason} onChange={e => setDestroyReason(e.target.value)} rows={2}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          </label>
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600">Type <b className="font-mono">{d.dossier_number}</b> to confirm</span>
            <input value={destroyConfirm} onChange={e => setDestroyConfirm(e.target.value)}
                   className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono" />
          </label>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setDestroying(false)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
            <button disabled={destroyConfirm.trim() !== d.dossier_number || destroyReason.trim().length < 5 || busy === 'destroy'}
                    onClick={async () => {
                      if (await run('destroy', () => api.post(`${base}/destroy`,
                        { basis, reason: destroyReason, confirm: destroyConfirm }))) setDestroying(false)
                    }}
                    className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-40">
              Destroy permanently
            </button>
          </div>
        </Modal>
      )}

      {citing && (
        <Modal error={modalError} title={`What does ${citing.doc_number} cite?`} onClose={() => setCiting(null)}>
          <p className="text-sm text-gray-600">
            Links from this document to others — in this dossier, or in another of the project's. In the downloaded
            pack, those in this dossier become hyperlinks to the cited files; the others are named with their dossier.
          </p>
          {(() => {
            const pick = (x) => (
              <label key={x.doc_number} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={citePick.includes(x.doc_number)}
                       onChange={e => setCitePick(sel => (e.target.checked
                         ? [...sel, x.doc_number] : sel.filter(n => n !== x.doc_number)))} />
                <span className="font-mono text-xs text-gray-500">{x.doc_number}</span> {x.title}
              </label>
            )
            const here = d.items.filter(x => x.id !== citing.id)
            return (
              <div className="mt-3 space-y-3 max-h-80 overflow-y-auto">
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">In this dossier</p>
                  {here.length ? here.map(pick) : <p className="text-xs text-gray-400">No other documents here.</p>}
                </div>
                {citeOthers.map(o => (
                  <div key={o.id} className="space-y-1.5">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {o.dossier_number} <span className="normal-case font-normal">— {o.title}</span>
                    </p>
                    {o.items.map(pick)}
                  </div>
                ))}
              </div>
            )
          })()}
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setCiting(null)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
            <button disabled={busy === 'cite'}
                    onClick={async () => {
                      if (await run('cite', () => api.post(`${base}/items/${citing.id}/references`,
                        { doc_numbers: citePick }))) setCiting(null)
                    }}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40">
              Save
            </button>
          </div>
        </Modal>
      )}

      {deleting && (
        <Modal error={modalError} title={`Delete ${d.dossier_number}`} onClose={() => setDeleting(false)}>
          <p className="text-sm text-gray-600">
            This permanently deletes the dossier, its {d.items.length} document{d.items.length === 1 ? '' : 's'} and every
            file stored for it. It was never submitted, so there is no record of it to keep — only a line in the audit
            trail saying who deleted it, and when.
          </p>
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600">Type <b className="font-mono">{d.dossier_number}</b> to confirm</span>
            <input autoFocus value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)}
                   className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono" />
          </label>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setDeleting(false)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
            <button disabled={deleteConfirm.trim() !== d.dossier_number || busy === 'delete'} onClick={deleteDossier}
                    className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-40">
              Delete permanently
            </button>
          </div>
        </Modal>
      )}

      {editing && (
        <Modal error={modalError} title={`Correct ${editing.item.doc_number}`} onClose={() => setEditing(null)}>
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Title</span>
            <input autoFocus value={editing.title} onChange={e => setEditing(x => ({ ...x, title: e.target.value }))}
                   className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          </label>
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600">Type</span>
            <TypeSelect value={editing.type} types={types} disabled={!editing.item.id_editable}
                        onChange={v => setEditing(x => ({ ...x, type: v }))} className="mt-1 w-full" />
          </label>
          {!editing.item.id_editable
            ? <p className="text-xs text-gray-500 mt-1">The ID stays as it is — this document has been checked before.</p>
            : effectiveCode(editing) !== editing.item.doc_code && (
              <p className="text-xs text-amber-700 mt-1">The ID becomes {pcode}-{effectiveCode(editing)}-… · documents that cite it are updated.</p>
            )}
          <label className="flex items-center gap-2 text-sm text-gray-600 mt-3">
            <input type="checkbox" checked={editing.required} onChange={e => setEditing(x => ({ ...x, required: e.target.checked }))} />
            Required for submission
          </label>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setEditing(null)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
            <button disabled={!editing.title.trim() || busy === 'edit'}
                    onClick={async () => {
                      if (await run('edit', () => api.patch(`${base}/items/${editing.item.id}`, {
                        title: editing.title, required: editing.required,
                        ...(editing.item.id_editable ? { code: effectiveCode(editing) } : {}),
                      }))) setEditing(null)
                    }}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40">
              Save
            </button>
          </div>
        </Modal>
      )}

      {adding && (
        <Modal error={modalError} title={`Add ${adding.length} file${adding.length === 1 ? '' : 's'} to ${d.dossier_number}`}
               onClose={() => setAdding(null)} wide>
          <p className="text-sm text-gray-600 flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-green-600 shrink-0" />
            Each file goes into the slot it matches. Check them, then add — nothing is read or sent anywhere.
          </p>
          <div className="mt-3 rounded-xl border border-gray-200 divide-y divide-gray-100">
            {adding.map(r => (
              <div key={r.key} className="p-3">
                <div className="flex items-center gap-2 text-sm">
                  <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className="truncate flex-1 min-w-0 text-gray-800">{r.file.name}</span>
                  <span className="text-xs text-gray-400 shrink-0">{sizeText(r.file.size)}</span>
                  <select value={r.target}
                          onChange={e => { const v = e.target.value; setAdding(rs => rs.map(x => (x.key === r.key ? { ...x, target: v } : x))) }}
                          className="w-72 rounded-lg border border-gray-300 px-2 py-1.5 text-sm bg-white">
                    {freeSlots.map(i => <option key={i.id} value={String(i.id)}>Into {i.doc_number} — {i.title}</option>)}
                    {d.can_shape && <option value="new">As a new document</option>}
                    <option value="skip">Don't add</option>
                  </select>
                </div>
                {r.target === 'new' && (
                  <div className="mt-2 flex items-center gap-2 pl-6">
                    <input value={r.title}
                           onChange={e => { const v = e.target.value; setAdding(rs => rs.map(x => (x.key === r.key ? { ...x, title: v } : x))) }}
                           className="flex-1 min-w-0 rounded-lg border border-gray-300 px-3 py-1.5 text-sm" placeholder="Document title" />
                    <TypeSelect value={r.type} types={types} className="w-56"
                                onChange={v => setAdding(rs => rs.map(x => (x.key === r.key ? { ...x, type: v } : x)))} />
                    <span className="text-xs font-mono text-gray-400 shrink-0">{pcode}-{effectiveCode(r)}-…</span>
                  </div>
                )}
                {r.file.size > MAX_BYTES && <p className="text-xs text-red-600 mt-1 pl-6">Too large — 50 MB at most.</p>}
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setAdding(null)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
            <button disabled={busy === 'add' || adding.every(r => r.target === 'skip') || adding.some(r => r.target !== 'skip' && r.file.size > MAX_BYTES)}
                    onClick={runAdd}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40 inline-flex items-center gap-2">
              {busy === 'add' && <Loader2 className="w-4 h-4 animate-spin" />}
              Add {adding.filter(r => r.target !== 'skip').length} file{adding.filter(r => r.target !== 'skip').length === 1 ? '' : 's'}
            </button>
          </div>
        </Modal>
      )}

      {fixing && (
        <Modal error={modalError} title="Correct the recipient" onClose={() => setFixing(null)}>
          <p className="text-sm text-gray-600">The dossier stays locked. The old recipient, the new one and your reason are kept in the audit trail.</p>
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600">Submitted to</span>
            <input autoFocus value={fixing.to} onChange={e => setFixing(x => ({ ...x, to: e.target.value }))}
                   className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          </label>
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600">Why it is being corrected</span>
            <input value={fixing.reason} onChange={e => setFixing(x => ({ ...x, reason: e.target.value }))}
                   className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="e.g. The wrong text was pasted" />
          </label>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setFixing(null)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
            <button disabled={!fixing.to.trim() || fixing.reason.trim().length < 5 || busy === 'fix'}
                    onClick={async () => {
                      if (await run('fix', () => api.post(`${base}/recipient`, { submitted_to: fixing.to, reason: fixing.reason })))
                        setFixing(null)
                    }}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40">
              Save correction
            </button>
          </div>
        </Modal>
      )}

      {reviewing === 'schedule' && (
        <Modal error={modalError} title="Periodic review" onClose={() => setReviewing(null)}>
          <p className="text-sm text-gray-600">
            How often this pack is checked to confirm it is still right. When a review falls due, the project’s managers
            are reminded by email.
          </p>
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600">Review it</span>
            <select value={reviewMonths} onChange={e => setReviewMonths(Number(e.target.value))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              <option value={0}>Never — no periodic review</option>
              {[6, 12, 24, 36].map(m => <option key={m} value={m}>Every {m} months</option>)}
            </select>
          </label>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setReviewing(null)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
            <button disabled={busy === 'schedule'}
                    onClick={async () => {
                      if (await run('schedule', () => api.post(`${base}/review-schedule`, { months: reviewMonths }))) setReviewing(null)
                    }}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40">
              Save
            </button>
          </div>
        </Modal>
      )}

      {reviewing === 'record' && (
        <Modal error={modalError} title={`Record a periodic review of ${d.dossier_number}`} onClose={() => setReviewing(null)}>
          <p className="text-sm text-gray-600">
            Say what you checked and what you found. It is kept in the audit trail{d.review_every_months
              ? `, and the next review falls due ${d.review_every_months} months from today` : ''}. A document that
            needs changing is revised for cause, as usual.
          </p>
          <textarea autoFocus value={reviewNote} onChange={e => setReviewNote(e.target.value)} rows={3}
                    className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    placeholder="e.g. Checked against the donor's 2027 template — still current" />
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setReviewing(null)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
            <button disabled={reviewNote.trim().length < 5 || busy === 'review'}
                    onClick={async () => {
                      if (await run('review', () => api.post(`${base}/periodic-review`, { note: reviewNote }))) setReviewing(null)
                    }}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40">
              Record review
            </button>
          </div>
        </Modal>
      )}

      {keeping && (
        <Modal error={modalError} title={`Keep ${d.dossier_number} longer`} onClose={() => setKeeping(null)}>
          <p className="text-sm text-gray-600">
            It is kept {d.retention_years} years now, until {formatDate(d.retain_until)}. Retention can only be lengthened —
            ending a dossier early is Destroy, with a privacy request or a legal instruction as the basis.
          </p>
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600">Keep it for</span>
            <select value={keeping.years} onChange={e => setKeeping(k => ({ ...k, years: Number(e.target.value) }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              {Array.from({ length: 50 - d.retention_years }, (_, i) => d.retention_years + 1 + i).map(y => (
                <option key={y} value={y}>{y} years</option>
              ))}
            </select>
          </label>
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600">Why (kept in the audit trail)</span>
            <input value={keeping.reason} onChange={e => setKeeping(k => ({ ...k, reason: e.target.value }))}
                   className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="e.g. The donor announced an audit for next year" />
          </label>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setKeeping(null)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
            <button disabled={keeping.reason.trim().length < 5 || busy === 'keep'}
                    onClick={async () => {
                      if (await run('keep', () => api.post(`${base}/retention`, { years: keeping.years, reason: keeping.reason }))) setKeeping(null)
                    }}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40">
              Keep longer
            </button>
          </div>
        </Modal>
      )}

      {ruling && (
        <Modal error={modalError} title={`Rules of ${d.dossier_number}`} onClose={() => setRuling(null)}>
          <p className="text-sm text-gray-600">Every change is written to the track record, with the old and the new setting.</p>
          {[['approver_id', 'Approver', 'Whoever manages the team'], ['backup_id', 'Backup, who stands in', 'None']].map(([k, label, none]) => (
            <label key={k} className="block mt-3">
              <span className="text-xs font-medium text-gray-600">{label}</span>
              <select value={ruling[k]} onChange={e => setRuling(r => ({ ...r, [k]: e.target.value ? Number(e.target.value) : '' }))}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                <option value="">{none}</option>
                {(d.team || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
          ))}
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600">{RULE_LABEL.steps}</span>
            <select value={ruling.steps} onChange={e => setRuling(r => ({ ...r, steps: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              <option value="one">One approval</option>
              <option value="three">Verify → review → approve</option>
            </select>
          </label>
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600">{RULE_LABEL.move}</span>
            <select value={ruling.move} onChange={e => setRuling(r => ({ ...r, move: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              <option value="team">Every team member agrees</option>
              <option value="approver">The approver alone</option>
            </select>
          </label>
          <fieldset className="mt-3">
            <legend className="text-xs font-medium text-gray-600">{RULE_LABEL.send_ways}</legend>
            {Object.entries(WAY_LABEL).map(([w, label]) => (
              <label key={w} className="flex items-center gap-2 text-sm text-gray-700 mt-1">
                <input type="checkbox" checked={ruling.send_ways.includes(w)}
                       onChange={e => setRuling(r => ({ ...r, send_ways: e.target.checked ? [...r.send_ways, w] : r.send_ways.filter(x => x !== w) }))} />
                {label}
              </label>
            ))}
          </fieldset>
          <fieldset className="mt-3">
            <legend className="text-xs font-medium text-gray-600">{RULE_LABEL.archivers}, besides the administrator</legend>
            <div className="max-h-32 overflow-y-auto">
              {(d.people || []).map(p => (
                <label key={p.id} className="flex items-center gap-2 text-sm text-gray-700 mt-1">
                  <input type="checkbox" checked={ruling.archivers.includes(p.id)}
                         onChange={e => setRuling(r => ({ ...r, archivers: e.target.checked ? [...r.archivers, p.id] : r.archivers.filter(x => x !== p.id) }))} />
                  {p.name}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setRuling(null)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
            <button disabled={!ruling.send_ways.length || busy === 'rules'}
                    onClick={async () => {
                      const body = { ...ruling, approver_id: ruling.approver_id || null, backup_id: ruling.backup_id || null }
                      if (await run('rules', () => api.post(`${base}/rules`, body))) setRuling(null)
                    }}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40">
              Save rules
            </button>
          </div>
        </Modal>
      )}

      {savingType && (
        <Modal error={modalError} title="Save as a ready-made pack" onClose={() => setSavingType(null)}>
          <p className="text-sm text-gray-600">
            Its list of documents — their types, which are required, and how long it is kept — is offered to everyone under
            New dossier. No file, name or signature goes with it.
          </p>
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600">Name</span>
            <input autoFocus value={savingType.name} onChange={e => setSavingType(s => ({ ...s, name: e.target.value }))}
                   className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          </label>
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600">What it is for (optional)</span>
            <input value={savingType.description} onChange={e => setSavingType(s => ({ ...s, description: e.target.value }))}
                   className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="e.g. A field mission's evidence for the donor" />
          </label>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setSavingType(null)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
            <button disabled={!savingType.name.trim() || busy === 'savetype'}
                    onClick={async () => {
                      if (await run('savetype', () => api.post(`${base}/save-as-type`, savingType))) setSavingType(null)
                    }}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40">
              Save
            </button>
          </div>
        </Modal>
      )}

      {addingSlot && (
        <Modal error={modalError} title="Add a document slot" onClose={() => setAddingSlot(false)}>
          <input autoFocus value={newSlot.title} placeholder="Document title — e.g. Site visit report"
                 onChange={e => { const v = e.target.value; setNewSlot(s => ({ ...s, title: v, type: s.touched ? s.type : suggestType(v, types) })) }}
                 className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          <div className="flex items-center gap-2 mt-3">
            <span className="text-xs text-gray-600">Type</span>
            <TypeSelect value={newSlot.type} types={types} onChange={v => setNewSlot(s => ({ ...s, type: v, touched: true }))} className="flex-1" />
          </div>
          <p className="text-xs text-gray-400 mt-1 font-mono">ID: {pcode}-{effectiveCode(newSlot)}-…</p>
          <label className="flex items-center gap-2 text-sm text-gray-600 mt-3">
            <input type="checkbox" checked={newSlot.required}
                   onChange={e => setNewSlot(s => ({ ...s, required: e.target.checked }))} /> Required for submission
          </label>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setAddingSlot(false)} className="px-3 py-2 text-sm text-gray-600">Cancel</button>
            <button disabled={!newSlot.title.trim()}
                    onClick={async () => {
                      if (await run('slot', () => api.post(`${base}/items`,
                        { code: effectiveCode(newSlot), title: newSlot.title.trim(), required: newSlot.required }))) {
                        setAddingSlot(false); setNewSlot({ title: '', type: 'GEN', required: true, touched: false })
                      }
                    }}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40">
              Add slot
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}

// ── the list ──────────────────────────────────────────────────────────────────

export default function DossiersPanel({ project, isArchived = false, initialDossierId = null }) {
  const { user } = useAuth()
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [creating, setCreating] = useState(false)
  const [openId, setOpenIdRaw] = useState(initialDossierId)
  // The open dossier lives in the address too, so a reload reopens it.
  const setOpenId = (id) => { setOpenIdRaw(id); setUrlParams({ view: 'dossiers', dossier: id || null }) }
  const [focusDoc, setFocusDoc] = useState(null)        // a document to bring into view, arriving from a citation
  const [note, setNote] = useState(null)
  const [showGone, setShowGone] = useState(false)

  // THE UNFINISHED DOSSIER. Held here, above the pop-up, so closing the pop-up cannot lose it, and
  // saved in this browser -- files included -- so closing the tab cannot either. Per person and per
  // project: several people share one browser here, and one person's draft is not another's.
  const draftKey = `dossier-draft-u${user?.id || 'anon'}-${project.id}`
  const [draft, setDraft] = useState(null)
  const [draftReady, setDraftReady] = useState(false)
  const [saveState, setSaveState] = useState(null)        // 'saved' · 'memory' (could not be saved in the browser)
  const everSaved = useRef(false)

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${project.id}/dossiers/`)
      setData(data)
    } catch (e) {
      setError(errText(e, 'Could not load the dossiers.'))
    }
  }
  useEffect(() => { load() }, [project?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {                                        // a draft left behind earlier comes back
    let live = true
    setDraftReady(false)
    loadFileDraft(draftKey).then(saved => {
      if (!live) return
      if (saved && hasContent(saved) && Date.now() - (saved.savedAt || 0) < DRAFT_DAYS * 86400000) {
        everSaved.current = true
        setDraft(saved); setSaveState('saved')
      } else if (saved) {
        deleteFileDraft(draftKey)                          // expired, or emptied
      }
      setDraftReady(true)
    })
    return () => { live = false }
  }, [draftKey])

  useEffect(() => {                                        // every change is saved by itself, once it settles
    if (!draftReady) return
    if (!hasContent(draft)) {
      if (everSaved.current) { everSaved.current = false; deleteFileDraft(draftKey); setSaveState(null) }
      return
    }
    everSaved.current = true
    const t = setTimeout(async () => {
      const ok = await saveFileDraft(draftKey, { ...draft, savedAt: Date.now() })
      setSaveState(ok ? 'saved' : 'memory')
    }, 700)
    return () => clearTimeout(t)
  }, [draft, draftReady, draftKey])

  useEffect(() => {                                        // if the browser could not keep it, say so before the tab goes
    if (saveState !== 'memory' || !hasContent(draft)) return
    const warn = (e) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [saveState, draft])

  const draftFiles = (draft?.rows || []).filter(r => r.file).length
  const discardDraft = () => {
    if (!window.confirm('Discard this unfinished dossier?\n\nEverything you added to it is forgotten — nothing has been uploaded yet.')) return
    setDraft(null)
  }

  if (openId) {
    return <DossierDetail projectId={project.id} dossierId={openId} isArchived={isArchived} focusDoc={focusDoc}
                          onOpenDossier={(id, docNumber) => { setFocusDoc(docNumber); setOpenId(id) }}
                          onBack={() => { setOpenId(null); setFocusDoc(null); load() }}
                          onDeleted={(msg) => { setOpenId(null); setNote(msg); load() }} />
  }

  const active = (data?.dossiers || []).filter(d => !d.destroyed)
  const gone = (data?.dossiers || []).filter(d => d.destroyed)

  const card = (d) => {
    const shown = shownStatus(d)
    const pct = d.counts.required ? Math.round((d.counts.approved / d.counts.required) * 100) : 0
    return (
      <button key={d.id} onClick={() => setOpenId(d.id)}
              className="text-left rounded-xl border border-gray-200 bg-white p-4 hover:border-blue-300 hover:shadow-sm">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs font-mono text-gray-500 flex items-center gap-1">
              {d.dossier_number}
              {d.hidden && <Lock className="w-3 h-3 text-gray-500" title="Hidden from the team" />}
            </p>
            <p className="font-medium text-gray-900 truncate">{d.title}</p>
            <p className="text-xs text-gray-500">{d.type_name}</p>
            {(d.review_due || d.retention_ended) && (
              <p className={`text-[11px] mt-0.5 ${d.retention_ended ? 'text-red-700' : 'text-amber-700'}`}>
                {d.retention_ended ? 'Retention ended — destroy, or keep longer' : 'Periodic review due'}
              </p>
            )}
            {d.direction === 'incoming' && (
              <p className="text-[11px] text-indigo-700 flex items-center gap-1 mt-0.5">
                <Inbox className="w-3 h-3" /> Received from {d.received_from}
              </p>
            )}
          </div>
          <span className={`text-[11px] px-2 py-0.5 rounded-full border whitespace-nowrap ${STATUS_BADGE[shown]}`}>
            {statusLabel(d)}
          </span>
        </div>
        {!d.destroyed && (
          <div className="mt-3 h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div className={`h-full ${pct === 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
          </div>
        )}
        <p className={`text-xs text-gray-500 ${d.destroyed ? 'mt-3' : 'mt-1.5'}`}>
          {d.destroyed ? '' : `${d.counts.approved}/${d.counts.required} approved · `}{d.counts.slots} documents
          {d.submitted_to && d.direction !== 'incoming' ? ` · sent to ${d.submitted_to}` : ''}
        </p>
      </button>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Dossiers</h3>
          <p className="text-xs text-gray-500 mt-0.5 max-w-2xl">
            A dossier is a pack of documents assembled, approved and submitted as one. Every document gets its own ID,
            every step is signed and time-stamped, and the whole pack downloads in one go.
          </p>
        </div>
        {data?.can_create && !isArchived && (
          <button onClick={() => setCreating(true)}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Plus className="w-4 h-4" /> New dossier
          </button>
        )}
      </div>

      {note && (
        <div className="bg-green-50 border border-green-200 text-green-800 text-sm px-4 py-2.5 rounded-lg flex items-center justify-between">
          {note}
          <button onClick={() => setNote(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      {draft && hasContent(draft) && !creating && !isArchived && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 flex flex-wrap items-center gap-3">
          <FileClock className="w-5 h-5 text-amber-700 shrink-0" />
          <div className="min-w-0 mr-auto">
            <p className="text-sm font-medium text-gray-900 truncate">Unfinished dossier — {draft.title || 'Untitled'}</p>
            <p className="text-xs text-gray-600">
              {draft.rows.length} document{draft.rows.length === 1 ? '' : 's'} · {draftFiles} file{draftFiles === 1 ? '' : 's'} ready ·{' '}
              {saveState === 'memory' ? 'kept only while this page stays open' : 'saved in this browser — nothing has been uploaded yet'}
            </p>
          </div>
          <button onClick={() => setCreating(true)}
                  className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700">Continue</button>
          <button onClick={discardDraft} className="px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-amber-100">Discard</button>
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
      {!data && !error && <p className="text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>}

      {data && active.length === 0 && gone.length === 0 && (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-6">
          <p className="text-sm font-medium text-gray-900">No dossiers on this project yet.</p>
          <div className="grid sm:grid-cols-3 gap-3 mt-3">
            {[['1', 'Drop in your files', 'Each one is named from its file name — or start from a ready-made pack.'],
              ['2', 'Check and approve', 'Verify, review and sign — one at a time, or all together.'],
              ['3', 'Submit and download', 'Send it to a named recipient and download the whole pack.']].map(([n, t, s2]) => (
              <div key={n} className="rounded-lg bg-gray-50 p-3">
                <p className="text-xs font-semibold text-blue-700">Step {n}</p>
                <p className="text-sm font-medium text-gray-900">{t}</p>
                <p className="text-xs text-gray-500 mt-0.5">{s2}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {active.length > 0 && <div className="grid md:grid-cols-2 gap-3">{active.map(card)}</div>}

      {gone.length > 0 && (
        <div>
          <button onClick={() => setShowGone(v => !v)} className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1.5">
            <Trash2 className="w-4 h-4" /> Destroyed ({gone.length}) — records only {showGone ? '▾' : '▸'}
          </button>
          {showGone && <div className="grid md:grid-cols-2 gap-3 mt-2">{gone.map(card)}</div>}
        </div>
      )}

      {creating && (
        <NewDossier projectId={project.id} projectCode={data?.project_code || ''} projectName={project.name}
                    initial={draft} saveState={saveState} onDraftChange={setDraft}
                    onClose={() => setCreating(false)}
                    onCreated={(id) => { setCreating(false); setOpenId(id) }} />
      )}
    </div>
  )
}
